import {
    useState,
    useCallback,
    useEffect,
    useRef,
    useReducer,
    useMemo,
    Suspense,
    memo,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMobile } from '../hooks/useResponsiveDesign';
import { Button } from '@/components/ui/Button';
import {
    RefreshCw,
    BookOpen,
    Info,
    Loader2,
    CheckCircle,
    AlertCircle,
    Newspaper,
    Globe2,
    ArrowUpRight,
    Clock3,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ChartErrorBoundary } from '@/components/ui/ChartErrorBoundary';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import {
    DashboardCardSkeleton,
    ChartSkeleton,
    PositionsSkeleton,
    HistorySkeleton,
} from '@/components/ui/skeleton';
import { useNFTVerificationContext } from '@/contexts/NFTVerificationContext';
import { toastError, toastSuccess } from '@/components/ui/use-toast';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { formatNumber } from '@/lib/utils';

import {
    getDashboardBatch,
    getRealBalance,
    type BatchResponseData,
    type DashboardMarketPrice,
} from '@/api/trading.service';
import DeactivatedAccountAlert from '@/components/DeactivatedAccountAlert';

import { useAutoTradingNotifications } from '../hooks/useAutoTradingNotifications';
import { API_BASE_URL } from '../config';
import {
    getAutoTradingStatus,
    toggleAutoTrading,
    togglePaperAutoTrading,
    type AutoTradingToggleResponse,
    type AutoTradingStatusResponse,
} from '../services/api';
import { usePaperTrading } from '../hooks/usePaperTrading';
import { paperTradingApi } from '../services/paperTradingApi';
import RealTimePerformanceMetrics from '../components/RealTimePerformanceMetrics';
import GridMetricsPanel from '../components/GridMetricsPanel';
import LiveBTCPrice from '../components/LiveBTCPrice';
import { useWebSocket } from '@/hooks/useWebSocket';
import { requestCache, useLoadingConsolidation } from '@/utils/cache';
import { lazyWithChunkRecovery } from '@/utils/lazyWithChunkRecovery';
import { authenticatedFetch, isAuthExpiredError } from '@/utils/authFetch';
import { priceService } from '@/services/price.service';
import type {
    BalanceUpdateEvent,
    PositionUpdateEvent,
    PriceUpdateEvent,
} from '@/services/websocketService';
import websocketService from '@/services/websocketService';

import PaperTradingHelp from '../components/PaperTradingHelp';
import TradingSignalStatusCard from '@/components/TradingSignalStatusCard';
import { ActivePosition } from '@/components/ActivePosition';
import type { Position } from '@/components/ActivePosition/types';
// Lazy load live trading chart component (lightweight-charts candlestick)
const ForecastChartSection = lazyWithChunkRecovery(
    () =>
        import('@/components/TradingChart/TradingChart').then(mod => ({
            default: mod.TradingChart,
        })),
    'dashboard-trading-chart',
);
const RiskSettingsPanel = lazyWithChunkRecovery(
    () => import('./DashboardRiskSettings').then(mod => ({ default: mod.RiskSettingsPanel })),
    'dashboard-risk-settings',
);
const SLTPStatisticsPanel = lazyWithChunkRecovery(
    () => import('./DashboardSLTPStats'),
    'dashboard-sltp-stats',
);
const TradingHistory = lazyWithChunkRecovery(
    () => import('./DashboardTradingHistory').then(mod => ({ default: mod.TradingHistory })),
    'dashboard-trading-history',
);
const PerformanceAnalyticsSection = lazyWithChunkRecovery(
    () => import('../components/PerformanceAnalytics'),
    'performance-analytics',
);

const getRuntimeEnvValue = (key: string): string | undefined => {
    try {
        const env = Function(`
      try {
        return import.meta && import.meta.env ? import.meta.env : {};
      } catch (_error) {
        return {};
      }
    `)() as Record<string, string | undefined>;
        return env?.[key];
    } catch (_error) {
        return undefined;
    }
};

const USE_WEBSOCKET_PNL_GUARDS = true;
const ENABLE_LIVE_TRADE_TEST = getRuntimeEnvValue('VITE_ENABLE_LIVE_TRADE_TEST') === 'true';
// Hide the "Advanced SL/TP Performance" panel from the dashboard.
// The component, endpoint, and batch section stay intact - flip to true to restore.
const SHOW_SLTP_STATS = false;

const DEFAULT_SYMBOL = 'BTCUSDT';
const DEFAULT_TIMEFRAME = '1h';
const SHARED_BTC_PRICE_STALE_AFTER_MS = 15000;

/**
 * Pair selector buttons (BTC/ETH/BNB/SOL) driven by the backend pair matrix.
 * Rendered in the header row and on the Price Forecast module; all instances
 * share the same symbol state, so they are always in sync.
 */
const SymbolSelectorGroup = memo(function SymbolSelectorGroup({
    options,
    activeSymbol,
    onSelect,
}: {
    options: string[];
    activeSymbol: string;
    onSelect: (symbol: string) => void;
}) {
    const { t } = useTranslation();

    return (
        <div
            className="flex items-center gap-1"
            role="group"
            aria-label={t('dashboard.symbolSelector')}
            data-testid="symbol-selector-group"
        >
            {options.map(option => {
                const optionSymbol = String(option).toUpperCase();
                const isActive = optionSymbol === activeSymbol.toUpperCase();
                return (
                    <Button
                        key={optionSymbol}
                        size="sm"
                        variant={isActive ? 'default' : 'ghost'}
                        className="h-7 px-2.5 text-xs font-semibold"
                        disabled={isActive}
                        onClick={() => onSelect(optionSymbol)}
                        title={t('dashboard.tooltips.symbolSelector')}
                    >
                        {optionSymbol.replace(/USDT$/, '')}
                    </Button>
                );
            })}
        </div>
    );
});
const PRIMARY_BATCH_SECTIONS = [
    'statistics',
    'risk_settings',
    'auto_trading_status',
    'market_price',
] as const;
const SECONDARY_BATCH_SECTIONS = ['signal_status', 'market_news', 'sltp_stats'] as const;
const DASHBOARD_WS_CHANNELS = ['balance', 'positions', 'prices'] as const;
const BALANCE_UNAVAILABLE_LABEL = 'Unavailable';

export const hasDeferredDashboardSections = (batchData?: BatchResponseData): boolean =>
    batchData?.signal_status !== undefined &&
    batchData?.market_news !== undefined &&
    batchData?.sltp_stats !== undefined;

export const mergeDashboardBatchData = (
    previous: BatchResponseData | undefined,
    incoming: BatchResponseData | undefined,
): BatchResponseData | undefined => {
    if (!incoming) return previous;
    if (!previous) return incoming;

    return {
        ...previous,
        ...incoming,
        meta: {
            ...previous.meta,
            ...incoming.meta,
            has_errors: Boolean(previous.meta?.has_errors || incoming.meta?.has_errors),
        },
    };
};

const formatDashboardDateTime = (value?: string | null): string => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const DeferredSectionUnavailableCard = ({
    title,
    message,
    action,
}: {
    title: string;
    message: string;
    action?: ReactNode;
}) => (
    <Card className="mb-6">
        <CardContent className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:justify-between">
            <div>
                <div className="text-sm font-semibold">{title}</div>
                <div className="mt-1 text-sm text-muted-foreground">{message}</div>
            </div>
            {action ?? null}
        </CardContent>
    </Card>
);

const getDashboardMarketPriceTimestamp = (
    marketPrice: DashboardMarketPrice | null | undefined,
): number => {
    const timestamp = Number(marketPrice?.timestamp ?? NaN);
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
};

const normalizeDashboardMarketPrice = (raw: unknown): DashboardMarketPrice | null => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const marketPrice = raw as DashboardMarketPrice;
    const price = Number(
        marketPrice.mark_price ?? marketPrice.markPrice ?? marketPrice.price ?? NaN,
    );
    if (!Number.isFinite(price) || price <= 0) {
        return null;
    }

    const staleAgeMs = Number(marketPrice.stale_age_ms ?? NaN);

    return {
        ...marketPrice,
        symbol:
            typeof marketPrice.symbol === 'string' && marketPrice.symbol
                ? marketPrice.symbol.toUpperCase()
                : DEFAULT_SYMBOL,
        price,
        mark_price: price,
        markPrice: price,
        is_stale: Boolean(marketPrice.is_stale),
        stale_age_ms: Number.isFinite(staleAgeMs) && staleAgeMs >= 0 ? staleAgeMs : undefined,
    };
};

export const pickPreferredDashboardMarketPrice = (
    current: DashboardMarketPrice | null | undefined,
    incoming: DashboardMarketPrice | null | undefined,
): DashboardMarketPrice | null => {
    if (!incoming) return current ?? null;
    if (incoming.is_stale) return current ?? null;
    if (!current) return incoming;

    const currentIsStale = Boolean(current.is_stale);
    const incomingIsStale = Boolean(incoming.is_stale);
    if (currentIsStale !== incomingIsStale) {
        return incomingIsStale ? current : incoming;
    }

    const currentTimestamp = getDashboardMarketPriceTimestamp(current);
    const incomingTimestamp = getDashboardMarketPriceTimestamp(incoming);
    if (currentTimestamp > 0 && incomingTimestamp > 0 && incomingTimestamp < currentTimestamp) {
        return current;
    }
    if (currentTimestamp > 0 && incomingTimestamp === 0) {
        return current;
    }

    const currentPrice = getResolvedDashboardMarketPrice(current);
    const incomingPrice = getResolvedDashboardMarketPrice(incoming);
    if (
        currentTimestamp === incomingTimestamp &&
        currentPrice === incomingPrice &&
        currentIsStale === incomingIsStale
    ) {
        return current;
    }

    return incoming;
};

const getResolvedDashboardMarketPrice = (
    marketPrice: DashboardMarketPrice | null | undefined,
): number | null => {
    const value = Number(
        marketPrice?.mark_price ?? marketPrice?.markPrice ?? marketPrice?.price ?? NaN,
    );
    return Number.isFinite(value) && value > 0 ? value : null;
};

// Component loading timeout protection (15 seconds)
const COMPONENT_LOAD_TIMEOUT = 15000;

export default function Dashboard() {
    const { t } = useTranslation();
    const { isMobile } = useMobile();
    const [symbol, setSymbol] = useState<string>(DEFAULT_SYMBOL);
    const [timeframe] = useState(DEFAULT_TIMEFRAME);
    const selectedSymbolRef = useRef(DEFAULT_SYMBOL);

    useEffect(() => {
        selectedSymbolRef.current = symbol;
    }, [symbol]);

    // Live price for the symbol the user is currently viewing (header ticker).
    // BTC keeps its dedicated dashboardMarketPrice flow (grid metrics, risk
    // settings) untouched; this state only feeds the header + chart context.
    const [selectedSymbolPrice, setSelectedSymbolPrice] = useState<DashboardMarketPrice | null>(
        null,
    );
    const [batchData, setBatchData] = useState<BatchResponseData | undefined>(undefined);
    const [dashboardMarketPrice, setDashboardMarketPrice] = useState<DashboardMarketPrice | null>(
        null,
    );
    const [priceStreamErrorCount, setPriceStreamErrorCount] = useState(0);
    const [sharedPriceConnectionStatus, setSharedPriceConnectionStatus] = useState<
        'connected' | 'error' | 'loading'
    >('loading');
    const hasSeenPriceStreamConnectionRef = useRef(false);
    const sharedPriceWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const balanceUpdateHandlersRef = useRef<Set<(event: BalanceUpdateEvent) => void>>(new Set());
    const positionUpdateHandlersRef = useRef<Set<(event: PositionUpdateEvent) => void>>(new Set());
    const secondarySectionsKeyRef = useRef<string | null>(null);
    const [deferredSectionsUnavailable, setDeferredSectionsUnavailable] = useState<{
        key: string;
        message: string;
    } | null>(null);
    // Paper trading hook
    const {
        isPaperMode,
        isModeResolved,
        account: paperAccount,
        balance: paperBalance,
        updateBalance: updatePaperBalance,
        refreshData: refreshPaperTradingData,
        switchTradingMode,
        isLoading: paperTradingLoading,
    } = usePaperTrading();
    const resolvedTradingMode = isModeResolved ? (isPaperMode ? 'paper' : 'live') : null;
    const [modeRequestVersion, setModeRequestVersion] = useState(0);
    const modeRequestVersionRef = useRef(0);
    const showResolvedModeUi = isModeResolved && !paperTradingLoading;

    // Auto-trading state management
    const [autoTradingEnabled, setAutoTradingEnabled] = useState(false);
    const [autoTradingLoading, setAutoTradingLoading] = useState(false);
    const [liveAutoTrading, setLiveAutoTrading] = useState(false);
    const [paperAutoTrading, setPaperAutoTrading] = useState(false);

    // Debug log for main Dashboard paper mode state (removed for production security)

    // Force re-render key for mode-aware components
    const [modeChangeKey, setModeChangeKey] = useState(0);
    const [positionRefreshTrigger, setPositionRefreshTrigger] = useState(0);
    const [liveTestLoading, setLiveTestLoading] = useState(false);
    const [showLiveTradeTestModal, setShowLiveTradeTestModal] = useState(false);

    // Store unrealized PnL calculated by Active Positions to share with RealTimePerformanceMetrics
    const [sharedUnrealizedPnL, setSharedUnrealizedPnL] = useState<number>(0);
    // Active positions snapshot published by ActivePosition, consumed by the chart overlays.
    const [chartPositions, setChartPositions] = useState<Position[]>([]);
    const [paperRiskSettings, setPaperRiskSettings] = useState<{
        investmentPercentage: number | null;
        leverage: number | null;
    }>({ investmentPercentage: null, leverage: null });

    // Handle paper trading data refresh
    const handlePaperTradingRefresh = useCallback(async () => {
        // Refresh paper trading data (Virtual Balance, Profit and Loss cards)
        if (isPaperMode) {
            await refreshPaperTradingData();
        }
        // Trigger position refresh
        setPositionRefreshTrigger(prev => prev + 1);
        // Force re-render of mode-aware components
        setModeChangeKey(prev => prev + 1);
    }, [isPaperMode, refreshPaperTradingData]);

    useEffect(() => {
        setBatchData(undefined);
        setSharedUnrealizedPnL(0);
        secondarySectionsKeyRef.current = null;
        setDeferredSectionsUnavailable(null);
        setModeRequestVersion(prev => {
            const next = prev + 1;
            modeRequestVersionRef.current = next;
            return next;
        });
    }, [resolvedTradingMode]);

    useEffect(() => {
        let isMounted = true;

        const applySettings = (detail: any) => {
            const investment =
                typeof detail?.investmentPercentage === 'number'
                    ? detail.investmentPercentage
                    : null;
            const leverageValue = typeof detail?.leverage === 'number' ? detail.leverage : null;
            setPaperRiskSettings({
                investmentPercentage: investment,
                leverage: leverageValue,
            });
        };

        const handleSettingsUpdate = (event: Event) => {
            applySettings((event as CustomEvent).detail || {});
        };

        const loadInitialSettings = async () => {
            try {
                const response = await paperTradingApi.getPaperRiskSettings({
                    forceRefresh: true,
                    ttl: 0,
                });
                if (!isMounted) return;
                if (response?.success) {
                    applySettings({
                        investmentPercentage: response.investment_percentage,
                        leverage: response.leverage,
                    });
                }
            } catch (error) {
                console.error('[Dashboard] Failed to load paper risk settings:', error);
            }
        };

        if (isPaperMode) {
            loadInitialSettings();
            window.addEventListener('paperRiskSettingsUpdated', handleSettingsUpdate);
        } else {
            setPaperRiskSettings({ investmentPercentage: null, leverage: null });
        }

        return () => {
            isMounted = false;
            window.removeEventListener('paperRiskSettingsUpdated', handleSettingsUpdate);
        };
    }, [isPaperMode]);

    // Fetch auto-trading status
    const fetchAutoTradingStatus = useCallback(async (batchDataOverride?: BatchResponseData) => {
        try {
            let data: AutoTradingStatusResponse | undefined;

            const statusFromBatch = batchDataOverride?.auto_trading_status;
            if (statusFromBatch && !(statusFromBatch as any)?.error) {
                data = statusFromBatch;
            } else if ((statusFromBatch as any)?.error) {
                console.warn(
                    '[Dashboard] Batch auto-trading status includes error',
                    (statusFromBatch as any).error,
                );
            }

            if (!data) {
                data = await getAutoTradingStatus();
            }

            if (!data) {
                return;
            }

            // Store both flags for diagnostics
            const liveEnabled = data.auto_trading_enabled ?? false;
            const paperEnabled = data.paper_auto_trading_enabled ?? false;
            const serverPaperMode = data.paper_trading_mode ?? false;

            setLiveAutoTrading(liveEnabled);
            setPaperAutoTrading(paperEnabled);

            // Use server-reported paper_trading_mode to select the correct flag
            const enabled = serverPaperMode ? paperEnabled : liveEnabled;
            setAutoTradingEnabled(enabled);
        } catch (error) {
            // Silently fail for initial fetch
        }
    }, []); // Remove isPaperMode dependency to use server state

    // Listen for paper trading reset events and trigger comprehensive refresh
    useEffect(() => {
        const handlePaperTradingReset = () => {
            // Trigger comprehensive refresh of all components
            setPositionRefreshTrigger(prev => prev + 1);
            setModeChangeKey(prev => prev + 1);

            // Force reload of paper trading data
            if (isPaperMode) {
                // Small delay to ensure backend state is fully updated
                setTimeout(() => {
                    handlePaperTradingRefresh();
                }, 100);
            }

            // Refresh auto-trading status
            fetchAutoTradingStatus();
        };

        window.addEventListener('paperTradingReset', handlePaperTradingReset as EventListener);

        return () => {
            window.removeEventListener(
                'paperTradingReset',
                handlePaperTradingReset as EventListener,
            );
        };
    }, [isPaperMode, handlePaperTradingRefresh, fetchAutoTradingStatus]);

    // Track mode changes and force re-renders
    useEffect(() => {
        // Force re-render of mode-aware components
        setModeChangeKey(prev => prev + 1);
    }, [isPaperMode]);

    // Help modal state
    const [showPaperTradingHelp, setShowPaperTradingHelp] = useState(false);

    // Clear data modal state
    const [showClearDataModal, setShowClearDataModal] = useState(false);
    const [clearingData, setClearingData] = useState(false);

    // Centralized loading state tracker
    type ComponentLoadingStatus = {
        loading: boolean;
        failed: boolean;
        error: string | null;
    };

    type ComponentKey =
        'topGrid' | 'positions' | 'riskSettings' | 'sltpStats' | 'history' | 'forecast';

    type LoadingAction =
        | { type: 'LOADING_START'; component: ComponentKey }
        | { type: 'LOADING_SUCCESS'; component: ComponentKey }
        | { type: 'LOADING_ERROR'; component: ComponentKey; error: string }
        | { type: 'LOADING_TIMEOUT'; component: ComponentKey }
        | { type: 'RESET' }
        | { type: 'GLOBAL_TIMEOUT' };

    const initialLoadingState: Record<ComponentKey, ComponentLoadingStatus> = {
        topGrid: { loading: true, failed: false, error: null },
        positions: { loading: true, failed: false, error: null },
        riskSettings: { loading: true, failed: false, error: null },
        sltpStats: { loading: SHOW_SLTP_STATS, failed: false, error: null },
        history: { loading: true, failed: false, error: null },
        forecast: { loading: true, failed: false, error: null },
    };

    function loadingReducer(
        state: Record<ComponentKey, ComponentLoadingStatus>,
        action: LoadingAction,
    ): Record<ComponentKey, ComponentLoadingStatus> {
        switch (action.type) {
            case 'RESET':
                return { ...initialLoadingState };
            case 'LOADING_START':
                return {
                    ...state,
                    [action.component]: { loading: true, failed: false, error: null },
                };
            case 'LOADING_SUCCESS':
                return {
                    ...state,
                    [action.component]: { loading: false, failed: false, error: null },
                };
            case 'LOADING_ERROR':
                return {
                    ...state,
                    [action.component]: { loading: false, failed: true, error: action.error },
                };
            case 'LOADING_TIMEOUT':
                return {
                    ...state,
                    [action.component]: { loading: false, failed: true, error: 'Loading timeout' },
                };
            case 'GLOBAL_TIMEOUT': {
                const updated: Record<ComponentKey, ComponentLoadingStatus> = { ...state };
                (Object.keys(updated) as ComponentKey[]).forEach(key => {
                    if (updated[key].loading) {
                        updated[key] = { loading: false, failed: true, error: 'Loading timeout' };
                    }
                });
                return updated;
            }
            default:
                return state;
        }
    }

    const [componentLoadingStates, dispatchLoadingState] = useReducer(
        loadingReducer,
        initialLoadingState,
    );
    const [resolvedLiveBalanceUsd, setResolvedLiveBalanceUsd] = useState<number | null>(null);
    const loadingStateRef = useRef(componentLoadingStates);

    useEffect(() => {
        loadingStateRef.current = componentLoadingStates;
    }, [componentLoadingStates]);

    const topGridLoading = componentLoadingStates.topGrid.loading;
    const positionsLoading = componentLoadingStates.positions.loading;
    const riskSettingsLoading = componentLoadingStates.riskSettings.loading;
    const sltpStatsLoading = componentLoadingStates.sltpStats.loading;
    const historyLoading = componentLoadingStates.history.loading;

    // Timeout refs for loading protection
    const topGridTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const positionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const riskSettingsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const sltpStatsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const historyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const forecastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Global timeout ref for final safety net
    const globalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const GLOBAL_LOAD_TIMEOUT = 30000; // 30 seconds

    // Auto-trading notifications
    useAutoTradingNotifications(batchData?.auto_trading_notifications);

    const applyDashboardMarketPrice = useCallback((raw: unknown) => {
        const normalized = normalizeDashboardMarketPrice(raw);
        if (!normalized) {
            return;
        }

        setDashboardMarketPrice(prev => pickPreferredDashboardMarketPrice(prev, normalized));
    }, []);

    const markSharedPriceHealthy = useCallback(() => {
        setSharedPriceConnectionStatus('connected');
        if (sharedPriceWatchdogRef.current !== null) {
            clearTimeout(sharedPriceWatchdogRef.current);
        }
        sharedPriceWatchdogRef.current = setTimeout(() => {
            setSharedPriceConnectionStatus(
                priceService.getConnectionStatus() ? 'connected' : 'error',
            );
        }, SHARED_BTC_PRICE_STALE_AFTER_MS);
    }, []);

    useEffect(() => {
        const cachedPrice = priceService.getCurrentPrice(DEFAULT_SYMBOL);
        if (cachedPrice && !cachedPrice.is_stale) {
            applyDashboardMarketPrice(cachedPrice);
            markSharedPriceHealthy();
        }

        const unsubscribe = priceService.subscribe(DEFAULT_SYMBOL, (_symbol, priceData) => {
            applyDashboardMarketPrice(priceData);
            setPriceStreamErrorCount(0);
            markSharedPriceHealthy();
        });

        return () => {
            unsubscribe();
            if (sharedPriceWatchdogRef.current !== null) {
                clearTimeout(sharedPriceWatchdogRef.current);
                sharedPriceWatchdogRef.current = null;
            }
        };
    }, [applyDashboardMarketPrice, markSharedPriceHealthy]);

    const applySelectedSymbolPrice = useCallback((raw: unknown) => {
        const normalized = normalizeDashboardMarketPrice(raw);
        if (!normalized) {
            return;
        }
        setSelectedSymbolPrice(normalized);
    }, []);

    // Hydrate + subscribe to the selected symbol's live price (header ticker).
    useEffect(() => {
        const upperSymbol = symbol.toUpperCase();
        // Drop the previous symbol's price immediately so the header never shows
        // one symbol's price labeled as another.
        setSelectedSymbolPrice(null);

        const cachedPrice = priceService.getCurrentPrice(upperSymbol);
        if (cachedPrice && !cachedPrice.is_stale) {
            applySelectedSymbolPrice(cachedPrice);
        }

        const unsubscribe = priceService.subscribe(upperSymbol, (_symbol, priceData) => {
            applySelectedSymbolPrice(priceData);
        });

        return () => {
            unsubscribe();
        };
    }, [symbol, applySelectedSymbolPrice]);

    // Pair matrix offered by the backend (risk_settings batch section).
    // Drives the symbol switcher; falls back to BTC until the first batch loads.
    const availableSymbols = useMemo(() => {
        const raw = (batchData as unknown as { risk_settings?: { available_symbols?: unknown } })
            ?.risk_settings?.available_symbols;
        const symbols = Array.isArray(raw) ? raw.map(item => String(item).toUpperCase()) : [];
        const unique = Array.from(new Set(symbols));
        return unique.length > 0 ? unique : [DEFAULT_SYMBOL];
    }, [batchData]);

    const registerBalanceUpdateHandler = useCallback(
        (handler: (event: BalanceUpdateEvent) => void) => {
            balanceUpdateHandlersRef.current.add(handler);
            return () => {
                balanceUpdateHandlersRef.current.delete(handler);
            };
        },
        [],
    );

    const registerPositionUpdateHandler = useCallback(
        (handler: (event: PositionUpdateEvent) => void) => {
            positionUpdateHandlersRef.current.add(handler);
            return () => {
                positionUpdateHandlersRef.current.delete(handler);
            };
        },
        [],
    );

    const topGridStatsUpdaterRef = useRef<((updater: (prev: any) => any) => void) | null>(null);

    const handleRealTimePnLUpdate = useCallback((newPnl: number, mode: 'paper' | 'live') => {
        if (!topGridStatsUpdaterRef.current) {
            console.warn(
                '[Dashboard] WebSocket: No stats updater registered for real-time PnL update',
            );
            return;
        }

        topGridStatsUpdaterRef.current((prevStats: any) => {
            if (!prevStats) {
                console.warn('[Dashboard] WebSocket: No stats available for real-time PnL update');
                return prevStats;
            }

            return updateStatsFromRealtime(prevStats, newPnl, {
                mode,
                useGuards: USE_WEBSOCKET_PNL_GUARDS,
            });
        });
    }, []);

    const handleWebSocketBalanceUpdate = useCallback(
        (payload: BalanceUpdateEvent) => {
            if (!payload || !payload.balance) {
                return;
            }

            const { balance: balanceData } = payload;
            if (balanceData.mode === 'paper') {
                const newBalance = Number(balanceData.balance);
                if (Number.isFinite(newBalance)) {
                    updatePaperBalance(newBalance);
                }
            }

            if (
                USE_WEBSOCKET_PNL_GUARDS &&
                balanceData.mode === 'paper' &&
                typeof balanceData.balance === 'number'
            ) {
                handleRealTimePnLUpdate(balanceData.balance, 'paper');
            }

            if (balanceData.mode === 'live' || balanceData.mode === 'real') {
                const liveBalanceNumber = Number(balanceData.balance);
                if (Number.isFinite(liveBalanceNumber)) {
                    setResolvedLiveBalanceUsd(liveBalanceNumber);
                }
            }

            balanceUpdateHandlersRef.current.forEach(handler => {
                handler(payload);
            });
        },
        [updatePaperBalance],
    );

    const handleWebSocketPositionUpdate = useCallback(
        (payload: PositionUpdateEvent) => {
            if (!payload) {
                return;
            }

            const totalPnL = Number(payload.total_pnl ?? payload.metadata?.total_pnl);
            if (Number.isFinite(totalPnL)) {
                const mode: 'paper' | 'live' = isPaperMode ? 'paper' : 'live';
                handleRealTimePnLUpdate(totalPnL, mode);
            }

            positionUpdateHandlersRef.current.forEach(handler => {
                handler(payload);
            });
        },
        [handleRealTimePnLUpdate, isPaperMode],
    );

    const handleWebSocketPriceUpdate = useCallback(
        (payload: PriceUpdateEvent) => {
            if (!payload) {
                return;
            }

            const payloadSymbol = (payload.symbol || '').toUpperCase();
            // BTC feed keeps driving the BTC-anchored dashboard consumers.
            if (payloadSymbol === DEFAULT_SYMBOL) {
                applyDashboardMarketPrice(payload.price);
                setPriceStreamErrorCount(0);
            }
            // The selected symbol's feed drives the header ticker.
            if (payloadSymbol && payloadSymbol === selectedSymbolRef.current.toUpperCase()) {
                applySelectedSymbolPrice(payload.price);
                if (payloadSymbol !== DEFAULT_SYMBOL) {
                    setPriceStreamErrorCount(0);
                }
            }
        },
        [applyDashboardMarketPrice, applySelectedSymbolPrice],
    );

    const { isConnected: dashboardWsConnected, isConnecting: dashboardWsConnecting } = useWebSocket(
        {
            channels: [...DASHBOARD_WS_CHANNELS],
            onBalanceUpdate: handleWebSocketBalanceUpdate,
            onPositionUpdate: handleWebSocketPositionUpdate,
            onPriceUpdate: handleWebSocketPriceUpdate,
        },
    );

    useEffect(() => {
        if (isPaperMode) {
            setResolvedLiveBalanceUsd(null);
        }
    }, [isPaperMode]);

    useEffect(() => {
        if (dashboardWsConnected) {
            hasSeenPriceStreamConnectionRef.current = true;
            setPriceStreamErrorCount(0);
            return;
        }

        if (hasSeenPriceStreamConnectionRef.current) {
            setPriceStreamErrorCount(prev => prev + 1);
        }
    }, [dashboardWsConnected]);

    const btcPriceConnectionStatus: 'connected' | 'error' | 'loading' =
        dashboardWsConnected || sharedPriceConnectionStatus === 'connected'
            ? 'connected'
            : dashboardWsConnecting ||
                sharedPriceConnectionStatus === 'loading' ||
                !hasSeenPriceStreamConnectionRef.current
              ? 'loading'
              : dashboardMarketPrice
                ? 'error'
                : 'loading';

    // Fetch auto-trading status on mount and when mode changes
    useEffect(() => {
        if (!isModeResolved) {
            return;
        }
        fetchAutoTradingStatus();
    }, [fetchAutoTradingStatus, isModeResolved, isPaperMode]);

    useEffect(() => {
        if (isModeResolved && batchData) {
            fetchAutoTradingStatus(batchData);
        }
    }, [batchData, fetchAutoTradingStatus, isModeResolved]);

    useEffect(() => {
        if (!isModeResolved || !batchData?.meta) {
            return;
        }

        const requestVersion = modeRequestVersionRef.current;

        const secondaryKey = [
            batchData.meta.trading_mode,
            batchData.meta.symbol || symbol,
            batchData.meta.timeframe || timeframe,
        ].join(':');

        if (
            secondarySectionsKeyRef.current === secondaryKey &&
            hasDeferredDashboardSections(batchData)
        ) {
            setDeferredSectionsUnavailable(null);
            return;
        }

        if (deferredSectionsUnavailable?.key === secondaryKey) {
            return;
        }

        secondarySectionsKeyRef.current = secondaryKey;
        setDeferredSectionsUnavailable(null);
        let cancelled = false;
        const timeoutMs = 12000;

        const deferredRequest = Promise.race([
            getDashboardBatch(symbol, timeframe, {
                forceRefresh: true,
                include: [...SECONDARY_BATCH_SECTIONS],
            }),
            new Promise<never>((_, reject) => {
                window.setTimeout(
                    () =>
                        reject(
                            new Error(`Deferred dashboard sections timed out after ${timeoutMs}ms`),
                        ),
                    timeoutMs,
                );
            }),
        ]);

        void deferredRequest
            .then(secondaryBatch => {
                if (cancelled || modeRequestVersionRef.current !== requestVersion) {
                    return;
                }

                setDeferredSectionsUnavailable(null);
                setBatchData(prev => mergeDashboardBatchData(prev, secondaryBatch));
            })
            .catch(error => {
                console.warn('[Dashboard] Failed to load deferred dashboard sections', error);
                if (cancelled || modeRequestVersionRef.current !== requestVersion) {
                    return;
                }

                setDeferredSectionsUnavailable({
                    key: secondaryKey,
                    message:
                        error instanceof Error
                            ? error.message
                            : 'Optional dashboard sections are temporarily unavailable.',
                });
            });

        return () => {
            cancelled = true;
        };
    }, [
        batchData?.meta?.generated_at,
        batchData?.meta?.trading_mode,
        batchData?.meta?.symbol,
        batchData?.meta?.timeframe,
        deferredSectionsUnavailable?.key,
        isModeResolved,
        symbol,
        timeframe,
    ]);

    // Poll Market News when warming: 30s *3 with exponential backoff
    useEffect(() => {
        if (!batchData?.market_news || (batchData.market_news as any)?.error) return;
        if (batchData.market_news.status !== 'warming') return;
        if (!isModeResolved) return;
        let cancelled = false;
        let attempt = 0;
        const maxAttempts = 3;
        const poll = async () => {
            if (cancelled) return;
            attempt += 1;
            const delayMs = 30000 * Math.pow(1.5, attempt - 1); // 30s, 45s, 67s
            await new Promise(r => setTimeout(r, delayMs));
            if (cancelled) return;
            try {
                const { refreshMarketNews } = await import('@/api/trading.service');
                // Try forced refresh endpoint first, fallback to batch include
                try {
                    const data = await refreshMarketNews(symbol, timeframe);
                    if (!cancelled && data && data.status === 'ready') {
                        setBatchData(prev => (prev ? { ...prev, market_news: data } : prev));
                        return;
                    }
                } catch {
                    // ignore and try batch
                }
                const secondary = await getDashboardBatch(symbol, timeframe, {
                    forceRefresh: true,
                    include: ['market_news'],
                });
                if (
                    !cancelled &&
                    secondary.market_news &&
                    secondary.market_news.status === 'ready'
                ) {
                    setBatchData(prev => mergeDashboardBatchData(prev, secondary));
                    return;
                }
                if (attempt < maxAttempts && !cancelled) {
                    poll();
                }
            } catch {
                if (attempt < maxAttempts && !cancelled) poll();
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [
        batchData?.market_news?.status,
        batchData?.market_news?.generated_at,
        isModeResolved,
        symbol,
        timeframe,
    ]);

    // Handle auto-trading toggle with debouncing
    const handleAutoTradingToggle = useCallback(
        async (enabled: boolean) => {
            const prevEnabled = autoTradingEnabled;
            const prevLive = liveAutoTrading;
            const prevPaper = paperAutoTrading;
            try {
                setAutoTradingLoading(true);
                // Optimistically update UI
                setAutoTradingEnabled(enabled);
                if (isPaperMode) {
                    setPaperAutoTrading(enabled);
                } else {
                    setLiveAutoTrading(enabled);
                }

                // Call appropriate API based on mode and capture response
                let response: AutoTradingToggleResponse;
                if (isPaperMode) {
                    response = await togglePaperAutoTrading(enabled);
                } else {
                    response = await toggleAutoTrading(enabled);
                }

                // Store both flags from server response
                const liveEnabled = response.auto_trading_enabled || false;
                const paperEnabled = response.paper_auto_trading_enabled || false;
                const serverPaperMode =
                    response.paper_trading_mode !== undefined
                        ? response.paper_trading_mode
                        : isPaperMode;

                setLiveAutoTrading(liveEnabled);
                setPaperAutoTrading(paperEnabled);

                // Use server-reported paper_trading_mode to select displayed flag
                const actualEnabled = serverPaperMode ? paperEnabled : liveEnabled;
                setAutoTradingEnabled(actualEnabled);
                setAutoTradingLoading(false);
                try {
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new Event('refreshRiskSettings'));
                    }
                } catch (_e) {
                    // no-op
                }

                // Show success toast based on actual server state
                toastSuccess({
                    title: enabled
                        ? t('dashboard.autoTradingEnabled')
                        : t('dashboard.autoTradingDisabled'),
                    description: serverPaperMode
                        ? t('dashboard.paperAutoTradingStatusUpdated')
                        : t('dashboard.autoTradingStatusUpdated'),
                });

                // Invalidate cached batch payload and refresh
                requestCache.invalidate('/api/dashboard/batch*');
                try {
                    const updatedBatch = await getDashboardBatch(undefined, undefined, {
                        forceRefresh: true,
                        include: [...PRIMARY_BATCH_SECTIONS],
                    });
                    secondarySectionsKeyRef.current = null;
                    setBatchData(prev => mergeDashboardBatchData(prev, updatedBatch));
                } catch (batchError) {
                    console.warn(
                        '[Dashboard] Failed to refresh batch after auto-trading toggle',
                        batchError,
                    );
                }
            } catch (error: any) {
                // Revert UI to previous values
                setAutoTradingEnabled(prevEnabled);
                setLiveAutoTrading(prevLive);
                setPaperAutoTrading(prevPaper);

                if (error?.name === 'ForceRefreshError') {
                    // Ignore special cancellation if ever thrown
                    return;
                }
                const errorCode = error.errorCode || error.responseData?.error_code;

                if (errorCode === 'insufficient_balance') {
                    const currentBalance =
                        error.currentBalance || error.responseData?.current_balance || 0;
                    const minimumRequired =
                        error.minimumRequired || error.responseData?.minimum_required || 0;
                    toastError({
                        title: t('notifications.autoTrading.insufficientBalanceTitle'),
                        description: t('notifications.autoTrading.insufficientBalanceDescription', {
                            required: minimumRequired,
                            current: currentBalance,
                        }),
                    });
                } else if (errorCode === 'missing_credentials') {
                    toastError({
                        title: t('notifications.autoTrading.missingCredentialsTitle'),
                        description: t('notifications.autoTrading.missingCredentialsDescription'),
                    });
                } else if (
                    errorCode === 'balance_check_failed' ||
                    errorCode === 'balance_verification_failed'
                ) {
                    toastError({
                        title: t('notifications.autoTrading.balanceVerificationFailedTitle'),
                        description: t(
                            'notifications.autoTrading.balanceVerificationFailedDescription',
                        ),
                    });
                } else {
                    toastError({
                        title: t('notifications.autoTrading.errorTitle'),
                        description:
                            error.message || t('notifications.autoTrading.errorDescription'),
                    });
                }
            } finally {
                setAutoTradingLoading(false);
            }
        },
        [isPaperMode, t, autoTradingEnabled, liveAutoTrading, paperAutoTrading],
    );

    const handleLiveSignalTest = useCallback(async () => {
        if (isPaperMode) {
            toastError({
                title: 'Switch to LIVE mode',
                description: 'Live signal tests are only available when LIVE mode is active.',
            });
            return;
        }

        if (!liveAutoTrading) {
            toastError({
                title: 'Enable Live Auto-Trading',
                description:
                    'Turn on LIVE auto-trading first so the container will accept the test signal.',
            });
            return;
        }

        const token = localStorage.getItem('access_token');
        if (!token) {
            toastError({
                title: 'Not authenticated',
                description: 'Please log in again to trigger a live test signal.',
            });
            return;
        }

        try {
            setLiveTestLoading(true);

            const payload: Record<string, any> = {
                direction: 'BUY',
                move_pct: 1.5,
                confirm_text: 'OPEN LIVE',
            };
            const currentPrice = getResolvedDashboardMarketPrice(dashboardMarketPrice);
            if (
                typeof currentPrice === 'number' &&
                Number.isFinite(currentPrice) &&
                currentPrice > 0
            ) {
                payload.current_price = currentPrice;
            }

            const response = await fetch(`${API_BASE_URL}/api/trading/debug/live-signal-test`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                credentials: 'include',
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok || !data?.success) {
                throw new Error(data?.error || 'Live test failed');
            }

            toastSuccess({
                title: 'Live test signal sent',
                description: 'Check backend logs for the new [LIVE_ORDER] entries.',
            });
            setPositionRefreshTrigger(prev => prev + 1);
            setModeChangeKey(prev => prev + 1);
        } catch (error: any) {
            toastError({
                title: 'Live test failed',
                description: error?.message || 'Unable to trigger live test signal.',
            });
        } finally {
            setLiveTestLoading(false);
        }
    }, [dashboardMarketPrice, isPaperMode, liveAutoTrading]);

    const handleConfirmLiveTradeTest = useCallback(async () => {
        setShowLiveTradeTestModal(false);
        await handleLiveSignalTest();
    }, [handleLiveSignalTest]);
    // Subscription status state
    const [, setSubscriptionStatus] = useState<string | null>(null);

    // Loading coordination callbacks
    const dispatchLoadingSuccess = useCallback((component: ComponentKey) => {
        dispatchLoadingState({ type: 'LOADING_SUCCESS', component });
    }, []);

    const dispatchLoadingError = useCallback((component: ComponentKey, error: string) => {
        dispatchLoadingState({ type: 'LOADING_ERROR', component, error });
    }, []);

    const dispatchLoadingStart = useCallback((component: ComponentKey) => {
        dispatchLoadingState({ type: 'LOADING_START', component });
    }, []);

    const handleTopGridLoaded = useCallback(() => {
        if (topGridTimeoutRef.current) {
            clearTimeout(topGridTimeoutRef.current);
            topGridTimeoutRef.current = null;
        }
        dispatchLoadingSuccess('topGrid');
    }, [dispatchLoadingSuccess]);
    const handlePositionsLoaded = useCallback(() => {
        if (positionsTimeoutRef.current) {
            clearTimeout(positionsTimeoutRef.current);
            positionsTimeoutRef.current = null;
        }
        dispatchLoadingSuccess('positions');
    }, [dispatchLoadingSuccess]);
    const handleForecastLoaded = useCallback(() => {
        if (forecastTimeoutRef.current) {
            clearTimeout(forecastTimeoutRef.current);
            forecastTimeoutRef.current = null;
        }
        dispatchLoadingSuccess('forecast');
    }, [dispatchLoadingSuccess]);
    const handleRiskSettingsLoaded = useCallback(() => {
        if (riskSettingsTimeoutRef.current) {
            clearTimeout(riskSettingsTimeoutRef.current);
            riskSettingsTimeoutRef.current = null;
        }
        dispatchLoadingSuccess('riskSettings');
    }, [dispatchLoadingSuccess]);
    const handleSltpStatsLoaded = useCallback(() => {
        if (sltpStatsTimeoutRef.current) {
            clearTimeout(sltpStatsTimeoutRef.current);
            sltpStatsTimeoutRef.current = null;
        }
        dispatchLoadingSuccess('sltpStats');
    }, [dispatchLoadingSuccess]);
    const handleHistoryLoaded = useCallback(() => {
        if (historyTimeoutRef.current) {
            clearTimeout(historyTimeoutRef.current);
            historyTimeoutRef.current = null;
        }
        dispatchLoadingSuccess('history');
    }, [dispatchLoadingSuccess]);

    // Error handling callbacks
    const handleTopGridError = useCallback(
        (error: string) => {
            if (topGridTimeoutRef.current) {
                clearTimeout(topGridTimeoutRef.current);
                topGridTimeoutRef.current = null;
            }
            dispatchLoadingError('topGrid', error);
        },
        [dispatchLoadingError],
    );

    const handlePositionsError = useCallback(
        (error: string) => {
            if (positionsTimeoutRef.current) {
                clearTimeout(positionsTimeoutRef.current);
                positionsTimeoutRef.current = null;
            }
            dispatchLoadingError('positions', error);
        },
        [dispatchLoadingError],
    );

    const handleRiskSettingsError = useCallback(
        (error: string) => {
            if (riskSettingsTimeoutRef.current) {
                clearTimeout(riskSettingsTimeoutRef.current);
                riskSettingsTimeoutRef.current = null;
            }
            dispatchLoadingError('riskSettings', error);
        },
        [dispatchLoadingError],
    );

    const handleSltpStatsError = useCallback(
        (error: string) => {
            if (sltpStatsTimeoutRef.current) {
                clearTimeout(sltpStatsTimeoutRef.current);
                sltpStatsTimeoutRef.current = null;
            }
            dispatchLoadingError('sltpStats', error);
        },
        [dispatchLoadingError],
    );

    const handleHistoryError = useCallback(
        (error: string) => {
            if (historyTimeoutRef.current) {
                clearTimeout(historyTimeoutRef.current);
                historyTimeoutRef.current = null;
            }
            dispatchLoadingError('history', error);
        },
        [dispatchLoadingError],
    );

    const handleForecastError = useCallback(
        (error: string) => {
            if (forecastTimeoutRef.current) {
                clearTimeout(forecastTimeoutRef.current);
                forecastTimeoutRef.current = null;
            }
            dispatchLoadingError('forecast', error);
        },
        [dispatchLoadingError],
    );

    // Retry handlers
    const scheduleTimeout = useCallback(
        (component: ComponentKey, ref: React.MutableRefObject<NodeJS.Timeout | null>) => {
            if (ref.current) {
                clearTimeout(ref.current);
            }
            ref.current = setTimeout(() => {
                if (loadingStateRef.current[component].loading) {
                    dispatchLoadingState({ type: 'LOADING_TIMEOUT', component });
                }
            }, COMPONENT_LOAD_TIMEOUT);
        },
        [],
    );

    const handleTopGridRetry = useCallback(() => {
        dispatchLoadingStart('topGrid');
        scheduleTimeout('topGrid', topGridTimeoutRef);
    }, [scheduleTimeout]);

    // Compute loading progress for UI
    const getLoadingProgress = useCallback(() => {
        const entries = Object.entries(componentLoadingStates) as Array<
            [ComponentKey, { loading: boolean; failed: boolean; error: string | null }]
        >;
        const total = entries.length;
        const loaded = entries.filter(([, state]) => !state.loading).length;
        const loadingComponents = entries.filter(([, state]) => state.loading).map(([key]) => key);
        const failedComponents = entries.filter(([, state]) => state.failed).map(([key]) => key);
        const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;

        return {
            loaded,
            total,
            percentage,
            loadingComponents,
            failedComponents,
            isAnyLoading: loadingComponents.length > 0,
        };
    }, [componentLoadingStates]);

    const handlePositionsRetry = useCallback(() => {
        dispatchLoadingStart('positions');
        scheduleTimeout('positions', positionsTimeoutRef);
    }, [scheduleTimeout]);

    const handleRiskSettingsRetry = useCallback(() => {
        dispatchLoadingStart('riskSettings');
        scheduleTimeout('riskSettings', riskSettingsTimeoutRef);
    }, [scheduleTimeout]);

    const handleSltpStatsRetry = useCallback(() => {
        dispatchLoadingStart('sltpStats');
        scheduleTimeout('sltpStats', sltpStatsTimeoutRef);
    }, [scheduleTimeout]);

    const handleHistoryRetry = useCallback(() => {
        dispatchLoadingStart('history');
        scheduleTimeout('history', historyTimeoutRef);
    }, [scheduleTimeout]);

    const handleForecastRetry = useCallback(() => {
        dispatchLoadingStart('forecast');
        scheduleTimeout('forecast', forecastTimeoutRef);
    }, [scheduleTimeout]);

    // ...

    // Initialize component loading timeouts as safety net
    useEffect(() => {
        scheduleTimeout('topGrid', topGridTimeoutRef);
        scheduleTimeout('positions', positionsTimeoutRef);
        scheduleTimeout('riskSettings', riskSettingsTimeoutRef);
        scheduleTimeout('sltpStats', sltpStatsTimeoutRef);
        scheduleTimeout('history', historyTimeoutRef);
        scheduleTimeout('forecast', forecastTimeoutRef);

        // Global timeout as final safety net
        globalTimeoutRef.current = setTimeout(() => {
            dispatchLoadingState({ type: 'GLOBAL_TIMEOUT' });
        }, GLOBAL_LOAD_TIMEOUT);

        return () => {
            if (topGridTimeoutRef.current) clearTimeout(topGridTimeoutRef.current);
            if (positionsTimeoutRef.current) clearTimeout(positionsTimeoutRef.current);
            if (riskSettingsTimeoutRef.current) clearTimeout(riskSettingsTimeoutRef.current);
            if (sltpStatsTimeoutRef.current) clearTimeout(sltpStatsTimeoutRef.current);
            if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
            if (forecastTimeoutRef.current) clearTimeout(forecastTimeoutRef.current);
            if (globalTimeoutRef.current) clearTimeout(globalTimeoutRef.current);
        };
    }, [scheduleTimeout]);

    // Fetch subscription status from backend
    useEffect(() => {
        async function fetchSubscriptionStatus() {
            try {
                const response = await authenticatedFetch(
                    `${API_BASE_URL}/api/trading/tier/status`,
                    {
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    },
                );
                if (!response.ok) throw new Error('Failed to fetch subscription status');
                const data = await response.json();
                setSubscriptionStatus(data.status || data.tier || 'Unknown');
            } catch (err) {
                if (!isAuthExpiredError(err)) {
                    setSubscriptionStatus('Unknown');
                }
            }
        }
        fetchSubscriptionStatus();
    }, []);

    // Clear LIVE trading data function
    const clearLiveDataFunction = useCallback(async () => {
        try {
            setClearingData(true);

            const response = await authenticatedFetch(`${API_BASE_URL}/api/trading/clear-data`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response
                    .json()
                    .catch(() => ({ error: 'Failed to clear data' }));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const result = await response.json();

            // Show success message
            toastSuccess({
                title: 'Data Cleared Successfully',
                description: `Archived ${result.archived_count || 0} app trades. Your LIVE dashboard is now clean.`,
            });

            // Refresh dashboard data to show the clean state
            setPositionRefreshTrigger(prev => prev + 1);
            setModeChangeKey(prev => prev + 1);

            // Close modal
            setShowClearDataModal(false);
        } catch (error: any) {
            if (!isAuthExpiredError(error)) {
                console.error('Error clearing LIVE data:', error);
                toastError({
                    title: 'Failed to Clear Data',
                    description:
                        error.message || 'An error occurred while clearing your LIVE trading data',
                });
            }
        } finally {
            setClearingData(false);
        }
    }, []);

    // Note: Removed mobile redirect to ensure feature parity between desktop and mobile

    return (
        <div
            className={`${isMobile ? 'p-3 space-y-4 w-full min-w-0 max-w-full box-border' : 'p-6 space-y-6'} ${isMobile ? 'max-w-full overflow-x-hidden' : 'w-full overflow-x-visible'}`}
            style={isMobile ? { maxWidth: '100vw' } : {}}
        >
            {/* Paper Trading Mode Banner */}
            {showResolvedModeUi && isPaperMode && (
                <div
                    className={`bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg ${isMobile ? 'p-3' : 'p-4'} mb-4 w-full min-w-0 max-w-full overflow-hidden`}
                >
                    <div
                        className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'}`}
                    >
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="flex-shrink-0">
                                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3
                                    className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-orange-800 dark:text-orange-200`}
                                >
                                    {t('trading.paperTradingActive')}
                                </h3>
                                <p
                                    className={`${isMobile ? 'text-xs' : 'text-xs'} text-orange-600 dark:text-orange-300 mt-1 truncate`}
                                >
                                    {isMobile
                                        ? `Virtual: $${paperBalance?.toLocaleString() || '0'}`
                                        : `${t('trading.paperTradingDesc')} • Virtual Balance: $${paperBalance?.toLocaleString() || '0'}`}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPaperTradingHelp(true)}
                            className={`text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 flex-shrink-0 ${isMobile ? 'self-start' : ''}`}
                        >
                            <BookOpen
                                className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} ${isMobile ? '' : 'mr-1'}`}
                            />
                            {!isMobile && <span className="text-xs">{t('trading.learnMore')}</span>}
                        </Button>
                    </div>
                </div>
            )}

            {/* Loading Progress Indicator */}
            {(() => {
                const progress = getLoadingProgress();
                if (!progress.isAnyLoading) return null;

                const componentNames: Record<string, string> = {
                    topGrid: 'Dashboard Statistics',
                    positions: 'Active Positions',
                    riskSettings: 'Risk Settings',
                    sltpStats: 'SL/TP Statistics',
                    history: 'Trading History',
                    forecast: 'Forecast Chart',
                };

                return (
                    <Card className="mb-4 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                        Loading dashboard components... ({progress.loaded}/
                                        {progress.total})
                                    </span>
                                </div>
                                <span className="text-xs text-blue-600 dark:text-blue-400 font-mono">
                                    {progress.percentage}%
                                </span>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mb-3">
                                <div
                                    className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progress.percentage}%` }}
                                />
                            </div>

                            {/* Component Status List */}
                            <div className="flex flex-wrap gap-2 text-xs">
                                {Object.entries(componentLoadingStates)
                                    .filter(([key]) => SHOW_SLTP_STATS || key !== 'sltpStats')
                                    .map(([key, state]) => {
                                        const name = componentNames[key] || key;
                                        const Icon = state.failed
                                            ? AlertCircle
                                            : state.loading
                                              ? Loader2
                                              : CheckCircle;
                                        const iconClass = state.failed
                                            ? 'text-red-500 dark:text-red-400'
                                            : state.loading
                                              ? 'text-blue-500 dark:text-blue-400 animate-spin'
                                              : 'text-green-500 dark:text-green-400';

                                        return (
                                            <div key={key} className="flex items-center gap-1">
                                                <Icon className={`w-3 h-3 ${iconClass}`} />
                                                <span className="text-blue-700 dark:text-blue-300">
                                                    {name}
                                                </span>
                                                {state.failed && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-4 px-1 text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                        onClick={() => {
                                                            if (key === 'topGrid')
                                                                handleTopGridRetry();
                                                            else if (key === 'positions')
                                                                handlePositionsRetry();
                                                            else if (key === 'riskSettings')
                                                                handleRiskSettingsRetry();
                                                            else if (key === 'sltpStats')
                                                                handleSltpStatsRetry();
                                                            else if (key === 'history')
                                                                handleHistoryRetry();
                                                            else if (key === 'forecast')
                                                                handleForecastRetry();
                                                        }}
                                                    >
                                                        Retry
                                                    </Button>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </CardContent>
                    </Card>
                );
            })()}

            <div
                className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'} mb-4 sm:mb-6`}
            >
                <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>
                    {t('dashboard.title')}
                </h1>
                <div
                    className={`${isMobile ? 'self-start' : ''} flex items-center gap-3 flex-wrap`}
                >
                    {/* Symbol switcher: drives the Price Forecast chart + header ticker.
               Options come from the backend pair matrix (risk_settings.available_symbols). */}
                    <SymbolSelectorGroup
                        options={availableSymbols}
                        activeSymbol={symbol}
                        onSelect={setSymbol}
                    />
                    <LiveBTCPrice
                        priceData={selectedSymbolPrice ?? dashboardMarketPrice}
                        connectionStatus={btcPriceConnectionStatus}
                        errorCount={priceStreamErrorCount}
                        symbolLabel={`${symbol.toUpperCase().replace(/USDT$/, '')}/USDT`}
                    />
                </div>

                {/* TEMP: Live signal test button – remove after debugging */}
                {ENABLE_LIVE_TRADE_TEST && showResolvedModeUi && !isPaperMode && (
                    <div className="flex items-center">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={liveTestLoading}
                            onClick={() => setShowLiveTradeTestModal(true)}
                        >
                            {liveTestLoading ? 'Triggering…' : 'Test LIVE Trade'}
                        </Button>
                    </div>
                )}
            </div>

            {/* Trading Mode and Auto-Trade Toggles */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center mb-4 sm:mb-6">
                {/* LIVE/PAPER Mode Toggle */}
                <div
                    className={`p-3 bg-gradient-to-r rounded-lg border ${
                        showResolvedModeUi && isPaperMode
                            ? 'from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-800'
                            : showResolvedModeUi
                              ? 'from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800'
                              : 'from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20 border-gray-200 dark:border-gray-800'
                    }`}
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                            <h4
                                className={`text-sm font-medium ${
                                    showResolvedModeUi && isPaperMode
                                        ? 'text-orange-800 dark:text-orange-200'
                                        : showResolvedModeUi
                                          ? 'text-blue-800 dark:text-blue-200'
                                          : 'text-gray-800 dark:text-gray-200'
                                }`}
                            >
                                {t('trading.tradingMode')}
                            </h4>
                            <p
                                className={`text-xs mt-1 ${
                                    showResolvedModeUi && isPaperMode
                                        ? 'text-orange-600 dark:text-orange-300'
                                        : showResolvedModeUi
                                          ? 'text-blue-600 dark:text-blue-300'
                                          : 'text-gray-600 dark:text-gray-300'
                                }`}
                            >
                                {showResolvedModeUi
                                    ? isPaperMode
                                        ? t('trading.paperMode')
                                        : t('trading.liveMode')
                                    : 'Syncing trading mode...'}
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={isPaperMode}
                                onChange={e => switchTradingMode(e.target.checked)}
                                disabled={!showResolvedModeUi || paperTradingLoading}
                            />
                            <div
                                className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                                    showResolvedModeUi && isPaperMode
                                        ? 'bg-orange-600 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 after:border-orange-300 dark:border-orange-600'
                                        : showResolvedModeUi
                                          ? 'bg-blue-600 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 after:border-blue-300 dark:border-blue-600'
                                          : 'bg-gray-300 dark:bg-gray-700 peer-focus:ring-gray-300 dark:peer-focus:ring-gray-700 after:border-gray-300 dark:border-gray-600'
                                } ${!showResolvedModeUi || paperTradingLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            ></div>
                            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300 whitespace-nowrap flex items-center gap-2">
                                {paperTradingLoading && (
                                    <svg
                                        className="animate-spin h-4 w-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                )}
                                {showResolvedModeUi
                                    ? isPaperMode
                                        ? t('trading.paperMode')
                                        : t('trading.liveMode')
                                    : 'Loading'}
                            </span>
                        </label>
                    </div>
                </div>

                {/* AUTO-trade Toggle */}
                <div
                    className={`p-3 rounded-lg border ${
                        showResolvedModeUi && autoTradingEnabled
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
                    }`}
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                            <h4
                                className={`text-sm font-medium ${
                                    showResolvedModeUi && autoTradingEnabled
                                        ? 'text-green-800 dark:text-green-200'
                                        : 'text-gray-800 dark:text-gray-200'
                                }`}
                            >
                                {showResolvedModeUi
                                    ? autoTradingEnabled
                                        ? t('trading.autoTradingActive')
                                        : t('trading.autoTradingDisabled')
                                    : 'Auto Trading'}
                            </h4>
                            <p
                                className={`text-xs mt-1 ${
                                    showResolvedModeUi && autoTradingEnabled
                                        ? 'text-green-600 dark:text-green-300'
                                        : 'text-gray-600 dark:text-gray-300'
                                }`}
                            >
                                {showResolvedModeUi
                                    ? autoTradingEnabled
                                        ? t('trading.autoTradingActiveDesc')
                                        : t('trading.autoTradingDisabledDesc')
                                    : 'Syncing auto-trading status...'}
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={autoTradingEnabled}
                                onChange={e => handleAutoTradingToggle(e.target.checked)}
                                disabled={
                                    !showResolvedModeUi || autoTradingLoading || paperTradingLoading
                                }
                            />
                            <div
                                className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                                    showResolvedModeUi && autoTradingEnabled
                                        ? 'bg-green-600 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 after:border-green-300 dark:border-green-600'
                                        : 'bg-gray-200 dark:bg-gray-700 peer-focus:ring-gray-300 dark:peer-focus:ring-gray-800 after:border-gray-300 dark:border-gray-600'
                                } ${!showResolvedModeUi || autoTradingLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            ></div>
                            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300 whitespace-nowrap flex items-center gap-2">
                                {autoTradingLoading && (
                                    <svg
                                        className="animate-spin h-4 w-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                )}
                                {showResolvedModeUi
                                    ? autoTradingEnabled
                                        ? t('common.toggleOn')
                                        : t('common.toggleOff')
                                    : '...'}
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Deactivated Account Alert */}
            <DeactivatedAccountAlert />
            {/* Top grid: Balance, PnL, Monthly Rate, Win Rate, Tier */}
            <div className="relative">
                {/* Skeleton overlay - shown until loaded */}
                {topGridLoading && (
                    <div className="absolute inset-0 z-10 bg-background">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <DashboardCardSkeleton />
                            <DashboardCardSkeleton />
                            <DashboardCardSkeleton />
                            <DashboardCardSkeleton />
                            <DashboardCardSkeleton />
                        </div>
                        <div className="flex gap-3 mt-6">
                            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded flex-1 animate-pulse"></div>
                            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
                        </div>
                    </div>
                )}
                {/* Always mount component - hidden by skeleton overlay until loaded */}
                <div
                    className={
                        topGridLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'
                    }
                >
                    <DashboardTopGrid
                        isPaperModeProp={isPaperMode}
                        isModeResolved={isModeResolved}
                        modeRequestVersion={modeRequestVersion}
                        paperBalanceProp={paperBalance}
                        paperRiskSettingsProp={paperRiskSettings}
                        symbolProp={symbol}
                        setBatchData={setBatchData}
                        onLoaded={handleTopGridLoaded}
                        onError={handleTopGridError}
                        onWebSocketBalanceUpdate={registerBalanceUpdateHandler}
                        onWebSocketPositionUpdate={registerPositionUpdateHandler}
                        onStatsUpdaterRegister={setter => {
                            topGridStatsUpdaterRef.current = setter;
                            return () => {
                                if (topGridStatsUpdaterRef.current === setter) {
                                    topGridStatsUpdaterRef.current = null;
                                }
                            };
                        }}
                        onResolvedLiveBalanceChange={setResolvedLiveBalanceUsd}
                    />
                </div>
            </div>
            {batchData?.market_news && !(batchData.market_news as any)?.error ? (
                <Card
                    className={`${isMobile ? 'mb-4 w-full min-w-0' : 'mb-6'} overflow-hidden border-slate-200/70 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.12),_transparent_35%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.14),_transparent_38%)] dark:border-slate-800`}
                >
                    <CardContent className={`${isMobile ? 'p-4' : 'p-5'} space-y-5`}>
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <div className="rounded-full bg-emerald-500/12 p-2 text-emerald-600 dark:text-emerald-300">
                                        <Newspaper className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold tracking-wide text-foreground">
                                            Market News Pulse
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Hourly headlines for market awareness only; they do not
                                            affect trade entries.
                                        </div>
                                        {batchData.market_news.status === 'warming' && (
                                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                                <span className="text-xs font-medium text-amber-600 dark:text-amber-300">
                                                    {batchData.market_news.message ||
                                                        'Market news is warming up and will refresh hourly.'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        try {
                                                            const { refreshMarketNews } =
                                                                await import('@/api/trading.service');
                                                            const data = await refreshMarketNews(
                                                                symbol,
                                                                timeframe,
                                                            );
                                                            if (data && data.status === 'ready') {
                                                                setBatchData(prev =>
                                                                    prev
                                                                        ? {
                                                                              ...prev,
                                                                              market_news: data,
                                                                          }
                                                                        : prev,
                                                                );
                                                                toastSuccess({
                                                                    title: 'Market news refreshed',
                                                                    description: `Loaded ${data.asset_news?.length ?? 0} asset + ${data.macro_news?.length ?? 0} macro headlines`,
                                                                });
                                                            } else if (data?.error) {
                                                                toastError({
                                                                    title: 'News refresh failed',
                                                                    description: String(data.error),
                                                                });
                                                            }
                                                        } catch (e: any) {
                                                            toastError({
                                                                title: 'News refresh failed',
                                                                description:
                                                                    e?.response?.data?.error ||
                                                                    e?.message ||
                                                                    'Please try again',
                                                            });
                                                        }
                                                    }}
                                                    className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/30"
                                                >
                                                    <RefreshCw className="h-3 w-3" /> Retry now
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs">
                                    <span className="rounded-full border border-border/70 bg-background/70 px-3 py-1 font-medium">
                                        {batchData.market_news.symbol || symbol}
                                    </span>
                                    <span className="rounded-full border border-border/70 bg-background/70 px-3 py-1 font-medium">
                                        {batchData.market_news.timeframe || timeframe}
                                    </span>
                                    <span className="rounded-full border border-border/70 bg-background/70 px-3 py-1 text-muted-foreground">
                                        Candle close:{' '}
                                        {formatDashboardDateTime(
                                            batchData.market_news.candle_close_at,
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                                <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
                                    <div className="flex items-center gap-2 font-medium text-foreground">
                                        <Clock3 className="h-3.5 w-3.5" />
                                        Last refresh
                                    </div>
                                    <div className="mt-1">
                                        {formatDashboardDateTime(
                                            batchData.market_news.generated_at,
                                        )}
                                    </div>
                                </div>
                                <div className="rounded-xl border border-border/60 bg-background/70 px-3 py-2">
                                    <div className="flex items-center gap-2 font-medium text-foreground">
                                        <RefreshCw className="h-3.5 w-3.5" />
                                        Next refresh
                                    </div>
                                    <div className="mt-1">
                                        {formatDashboardDateTime(
                                            batchData.market_news.next_refresh_at,
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid gap-4 xl:grid-cols-2">
                            <div className="rounded-2xl border border-border/60 bg-background/75 p-4 shadow-sm backdrop-blur">
                                <div className="mb-3 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-sm font-semibold">
                                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                                        Asset-specific
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {(batchData.market_news.asset_news || []).length} headlines
                                    </span>
                                </div>
                                <div className="space-y-3">
                                    {(batchData.market_news.asset_news || []).length ? (
                                        (batchData.market_news.asset_news || []).map(
                                            (item, index) => (
                                                <a
                                                    key={`${item.title}-${index}`}
                                                    href={item.url || '#'}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="group block rounded-xl border border-border/50 bg-background/80 p-3 transition-all hover:-translate-y-0.5 hover:border-emerald-400/50 hover:shadow-md"
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="line-clamp-2 text-sm font-medium text-foreground">
                                                                {item.title}
                                                            </div>
                                                            {item.summary && (
                                                                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                                                    {item.summary}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                                                        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-300">
                                                            {item.source || 'Source'}
                                                        </span>
                                                        <span>
                                                            {formatDashboardDateTime(
                                                                item.published_at,
                                                            )}
                                                        </span>
                                                    </div>
                                                </a>
                                            ),
                                        )
                                    ) : (
                                        <div className="rounded-xl border border-dashed border-border/60 p-4 text-sm text-muted-foreground">
                                            No asset-specific headlines cached for this cycle.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-2xl border border-border/60 bg-background/75 p-4 shadow-sm backdrop-blur">
                                <div className="mb-3 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-sm font-semibold">
                                        <Globe2 className="h-4 w-4 text-sky-500" />
                                        Macro and market
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {(batchData.market_news.macro_news || []).length} headlines
                                    </span>
                                </div>
                                <div className="space-y-3">
                                    {(batchData.market_news.macro_news || []).length ? (
                                        (batchData.market_news.macro_news || []).map(
                                            (item, index) => (
                                                <a
                                                    key={`${item.title}-${index}`}
                                                    href={item.url || '#'}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="group block rounded-xl border border-border/50 bg-background/80 p-3 transition-all hover:-translate-y-0.5 hover:border-sky-400/50 hover:shadow-md"
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="line-clamp-2 text-sm font-medium text-foreground">
                                                                {item.title}
                                                            </div>
                                                            {item.summary && (
                                                                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                                                    {item.summary}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                                                        <span className="rounded-full bg-sky-500/10 px-2 py-0.5 font-medium text-sky-700 dark:text-sky-300">
                                                            {item.source || 'Source'}
                                                        </span>
                                                        <span>
                                                            {formatDashboardDateTime(
                                                                item.published_at,
                                                            )}
                                                        </span>
                                                    </div>
                                                </a>
                                            ),
                                        )
                                    ) : (
                                        <div className="rounded-xl border border-dashed border-border/60 p-4 text-sm text-muted-foreground">
                                            No macro headlines cached for this cycle.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ) : deferredSectionsUnavailable ? (
                <DeferredSectionUnavailableCard
                    title="Market News Pulse"
                    message={`Cached market headlines are temporarily unavailable. ${deferredSectionsUnavailable.message}`}
                />
            ) : null}
            {/* Risk Settings */}
            <div className="relative">
                {/* Skeleton overlay - shown until loaded */}
                {riskSettingsLoading && (
                    <div className="absolute inset-0 z-10 bg-background">
                        <Card
                            className={`${isMobile ? 'mb-4 w-full min-w-0' : 'mb-6'} max-w-full overflow-hidden`}
                        >
                            <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
                                </div>
                                <div
                                    className={`grid grid-cols-1 ${isMobile ? 'gap-4' : 'md:grid-cols-2 gap-6'}`}
                                >
                                    <div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2 animate-pulse"></div>
                                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                                    </div>
                                    <div>
                                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2 animate-pulse"></div>
                                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-6">
                                    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded flex-1 animate-pulse"></div>
                                    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}
                {/* Always mount component - hidden by skeleton overlay until loaded */}
                <div
                    className={
                        riskSettingsLoading
                            ? 'opacity-0'
                            : 'opacity-100 transition-opacity duration-300'
                    }
                >
                    <ErrorBoundary
                        onError={() => handleRiskSettingsError('Component error')}
                        onReset={handleRiskSettingsLoaded}
                    >
                        <Suspense fallback={<DashboardCardSkeleton />}>
                            <RiskSettingsPanel
                                isPaperMode={isPaperMode}
                                currentBalanceUsd={
                                    isPaperMode
                                        ? typeof paperBalance === 'number' &&
                                          Number.isFinite(paperBalance)
                                            ? paperBalance
                                            : null
                                        : (() => {
                                              const liveBalance = Number(
                                                  batchData?.balance?.balance ?? NaN,
                                              );
                                              if (Number.isFinite(liveBalance)) {
                                                  return liveBalance;
                                              }
                                              return typeof resolvedLiveBalanceUsd === 'number' &&
                                                  Number.isFinite(resolvedLiveBalanceUsd)
                                                  ? resolvedLiveBalanceUsd
                                                  : null;
                                          })()
                                }
                                entryPriceUsd={getResolvedDashboardMarketPrice(
                                    dashboardMarketPrice,
                                )}
                                onPositionRefresh={() =>
                                    setPositionRefreshTrigger(prev => prev + 1)
                                }
                                onLoaded={handleRiskSettingsLoaded}
                                onError={handleRiskSettingsError}
                                onPaperRiskSettingsUpdate={setPaperRiskSettings}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </div>
            </div>

            {/* Performance Analytics: profit + drawdown equity curve, monthly returns, per-symbol breakdown */}
            <ErrorBoundary onError={() => undefined} onReset={() => undefined}>
                <Suspense fallback={<DashboardCardSkeleton />}>
                    <PerformanceAnalyticsSection
                        isPaperMode={isPaperMode}
                        key={`performance-analytics-${modeChangeKey}`}
                        className="mb-6"
                    />
                </Suspense>
            </ErrorBoundary>

            {/* SL/TP Statistics (hidden: no current use - flip SHOW_SLTP_STATS to restore) */}
            {SHOW_SLTP_STATS && (
                <div className="relative">
                    {/* Skeleton overlay - shown until loaded */}
                    {sltpStatsLoading && (
                        <div className="absolute inset-0 z-10 bg-background">
                            <Card className="w-full">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                        <DashboardCardSkeleton />
                                        <DashboardCardSkeleton />
                                        <DashboardCardSkeleton />
                                        <DashboardCardSkeleton />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                    {/* Always mount component - hidden by skeleton overlay until loaded */}
                    <div
                        className={
                            sltpStatsLoading
                                ? 'opacity-0'
                                : 'opacity-100 transition-opacity duration-300'
                        }
                    >
                        <ErrorBoundary
                            onError={() => handleSltpStatsError('Component error')}
                            onReset={handleSltpStatsLoaded}
                        >
                            <Suspense fallback={<DashboardCardSkeleton />}>
                                <SLTPStatisticsPanel
                                    isPaperMode={isPaperMode}
                                    onLoaded={handleSltpStatsLoaded}
                                    batchData={batchData}
                                />
                            </Suspense>
                        </ErrorBoundary>
                    </div>
                </div>
            )}
            {/* Forecast Chart */}
            {/* Trading Signal Status */}
            <TradingSignalStatusCard
                initialData={batchData?.signal_status ?? null}
                marketNews={batchData?.market_news ?? null}
                forecast={batchData?.forecast ?? null}
            />

            {/* Price Forecast header: section label + pair selector (mirrors the
                header switcher; both share the same symbol state) */}
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                    {t('dashboard.forecast.title')}
                </span>
                <SymbolSelectorGroup
                    options={availableSymbols}
                    activeSymbol={symbol}
                    onSelect={setSymbol}
                />
            </div>

            {/* Forecast Chart Section - Always mounted to avoid dependency on other loaders */}
            <ChartErrorBoundary key={`${symbol}-${timeframe}`} onReset={handleForecastRetry}>
                <Suspense fallback={<ChartSkeleton height="500px" />}>
                    <ForecastChartSection
                        symbol={symbol}
                        timeframe={timeframe}
                        marketPrice={selectedSymbolPrice ?? dashboardMarketPrice}
                        positions={chartPositions}
                        positionMode={isPaperMode ? 'paper' : 'live'}
                        onLoaded={handleForecastLoaded}
                        onError={handleForecastError}
                    />
                </Suspense>
            </ChartErrorBoundary>

            {showResolvedModeUi && (
                <RealTimePerformanceMetrics
                    key={`rtpm-${modeChangeKey}`}
                    mode={isPaperMode ? 'paper' : 'live'}
                    baseBalance={
                        isPaperMode
                            ? typeof paperBalance === 'number' && Number.isFinite(paperBalance)
                                ? paperBalance
                                : null
                            : typeof resolvedLiveBalanceUsd === 'number' &&
                                Number.isFinite(resolvedLiveBalanceUsd)
                              ? resolvedLiveBalanceUsd
                              : Number.isFinite(Number(batchData?.balance?.balance))
                                ? Number(batchData?.balance?.balance)
                                : null
                    }
                    winRate={
                        isPaperMode
                            ? Number(paperAccount?.win_rate ?? 0)
                            : Number(batchData?.statistics?.win_rate ?? 0)
                    }
                    unrealizedPnL={sharedUnrealizedPnL}
                />
            )}

            {/* Paper Trading Dashboard removed (duplicate of Real-Time Performance) */}

            {/* Grid Metrics (mode-agnostic; hides itself when no grid is active) */}
            <GridMetricsPanel
                className={isMobile ? 'w-full min-w-0 max-w-full overflow-hidden mb-4' : 'mb-6'}
                refreshTrigger={positionRefreshTrigger}
                initialData={batchData?.grid_metrics ?? null}
                canonicalBtcMarkPrice={getResolvedDashboardMarketPrice(dashboardMarketPrice)}
            />

            {/* Active open position - mode-aware */}
            <div className="relative">
                {/* Skeleton overlay - shown until loaded */}
                {positionsLoading && (
                    <div className="absolute inset-0 z-10 bg-background">
                        <PositionsSkeleton />
                    </div>
                )}
                {/* Always mount component - hidden by skeleton overlay until loaded */}
                <div
                    className={
                        positionsLoading
                            ? 'opacity-0'
                            : 'opacity-100 transition-opacity duration-300'
                    }
                >
                    <ActivePosition
                        key={`active-positions-${modeChangeKey}`}
                        refreshTrigger={positionRefreshTrigger}
                        isPaperMode={isPaperMode}
                        isModeResolved={isModeResolved}
                        modeRequestVersion={modeRequestVersion}
                        isRealtimeConnected={dashboardWsConnected}
                        dashboardMarketPrice={dashboardMarketPrice}
                        initialData={batchData?.positions ?? null}
                        onPositionsChange={setChartPositions}
                        onDataRefresh={handlePaperTradingRefresh}
                        onPositionRefresh={() => setPositionRefreshTrigger(prev => prev + 1)}
                        onModeChange={() => setModeChangeKey(prev => prev + 1)}
                        onUnrealizedPnLChange={setSharedUnrealizedPnL}
                        onLoaded={handlePositionsLoaded}
                        onError={handlePositionsError}
                        onWebSocketPositionUpdate={registerPositionUpdateHandler}
                    />
                </div>
            </div>
            {/* Profit Share Summary - Only shown in live mode */}
            {!isPaperMode && <ProfitShareSummary />}
            {/* Paginated trading history - mode-aware */}
            <div className="relative">
                {/* Skeleton overlay - shown until loaded */}
                {historyLoading && (
                    <div className="absolute inset-0 z-10 bg-background">
                        <HistorySkeleton />
                    </div>
                )}
                {/* Always mount component - hidden by skeleton overlay until loaded */}
                <div
                    className={
                        historyLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'
                    }
                >
                    <ErrorBoundary
                        onError={() => handleHistoryError('Component error')}
                        onReset={handleHistoryLoaded}
                    >
                        <Suspense fallback={<HistorySkeleton />}>
                            <TradingHistory
                                key={`trading-history-${modeChangeKey}-${positionRefreshTrigger}`}
                                onLoaded={handleHistoryLoaded}
                                onError={handleHistoryError}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </div>
            </div>

            {/* UI feedback for blocked users */}
            <BlockedUserAlert />

            {/* Paper Trading Help Modal */}
            <PaperTradingHelp
                isOpen={showPaperTradingHelp}
                onClose={() => setShowPaperTradingHelp(false)}
            />

            {/* Clear LIVE Data Confirmation Modal */}
            <ConfirmationModal
                isOpen={showClearDataModal}
                onClose={() => setShowClearDataModal(false)}
                onConfirm={clearLiveDataFunction}
                title="Clear LIVE Trading Data"
                description="This will archive all app-generated trades from your LIVE account to give you a clean start."
                confirmText="Clear Data"
                variant="danger"
                isLoading={clearingData}
            >
                <div className="space-y-3">
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                        <div className="text-sm text-yellow-800 dark:text-yellow-200">
                            <strong>What will be cleared:</strong>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                                <li>App-generated trades in Active Positions</li>
                                <li>Statistics showing in the top dashboard grid</li>
                                <li>Historical app trade records</li>
                            </ul>
                        </div>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                            <strong>What will remain:</strong>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                                <li>Your actual exchange account balance</li>
                                <li>External trades opened directly on your exchange</li>
                                <li>Your API credentials and settings</li>
                            </ul>
                        </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        This action cannot be undone, but your data will be safely archived.
                    </p>
                </div>
            </ConfirmationModal>

            <ConfirmationModal
                isOpen={showLiveTradeTestModal}
                onClose={() => setShowLiveTradeTestModal(false)}
                onConfirm={handleConfirmLiveTradeTest}
                title="Open LIVE Test Trade"
                description="This will dispatch a synthetic LIVE signal into the trading container and may open a real position on your Binance account."
                confirmText="Open Test Trade"
                cancelText="Cancel"
                variant="warning"
                requireTypedConfirmation
                typedConfirmationText="OPEN LIVE"
                isLoading={liveTestLoading}
            >
                <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                        Ensure you are in LIVE mode and LIVE auto-trading is enabled.
                    </p>
                    <p className="text-sm text-muted-foreground">
                        This is temporary. Disable by setting{' '}
                        <span className="font-mono">VITE_ENABLE_LIVE_TRADE_TEST</span> to{' '}
                        <span className="font-mono">false</span>.
                    </p>
                </div>
            </ConfirmationModal>
        </div>
    );
}

// Helper function to update stats from SSE P&L data
const updateStatsFromRealtime = (
    prevStats: any,
    newPnl: number,
    options: { mode: 'paper' | 'live'; useGuards: boolean },
) => {
    const { mode, useGuards } = options;

    if (useGuards) {
        if (mode === 'paper') {
            // GUARDED BEHAVIOR for paper mode: separate unrealized P&L
            const currentRealizedPnL = prevStats.total_pnl || prevStats.pnl || 0;

            // Only update if the new unrealized P&L is different and not overriding valid realized P&L with zero
            if (newPnl === 0 && currentRealizedPnL !== 0) {
                console.log(
                    '[DashboardTopGrid] WebSocket (GUARDED): Preventing override of realized P&L',
                    currentRealizedPnL,
                    'with zero unrealized P&L from WebSocket',
                );
                return prevStats; // Keep existing stats, don't override realized P&L
            }

            return {
                ...prevStats,
                // Store unrealized P&L separately - DON'T override total_pnl (realized P&L)
                unrealized_pnl: newPnl, // This is for active positions only
                // Keep the original realized P&L from completed trades
                total_pnl: currentRealizedPnL, // Preserve realized P&L from statistics API
                pnl: currentRealizedPnL, // Preserve realized P&L from statistics API
                // Preserve other stats like win_rate, monthly_rate, etc.
                pnl_today: prevStats.pnl_today || 0,
                win_rate: prevStats.win_rate || 0,
                winRate: prevStats.winRate || 0,
                monthly_rate: prevStats.monthly_rate || 0,
                monthlyRate: prevStats.monthlyRate || 0,
            };
        } else {
            // GUARDED BEHAVIOR for live mode: protect from zero overrides
            const currentPnL =
                prevStats.total_pnl ||
                prevStats.pnl ||
                prevStats.total_pnl_value ||
                prevStats.pnl_value ||
                0;

            // Only update if the new P&L is different and not overriding a valid value with zero
            if (newPnl === 0 && currentPnL !== 0) {
                console.log(
                    '[DashboardTopGrid] WebSocket (GUARDED): Preventing override of valid live P&L',
                    currentPnL,
                    'with zero from WebSocket',
                );
                return prevStats; // Keep existing stats, don't override with zero
            }

            return {
                ...prevStats,
                total_pnl_value: newPnl,
                pnl_value: newPnl,
                total_pnl: newPnl,
                pnl: newPnl,
            };
        }
    } else {
        // LEGACY BEHAVIOR: direct update without guarding
        if (mode === 'paper') {
            return {
                ...prevStats,
                total_pnl: newPnl,
                pnl: newPnl,
            };
        } else {
            return {
                ...prevStats,
                total_pnl_value: newPnl,
                pnl_value: newPnl,
                total_pnl: newPnl,
                pnl: newPnl,
            };
        }
    }
};

// Top grid: Balance, PnL, Monthly Rate, Win Rate, Tier
interface DashboardTopGridProps {
    isPaperModeProp: boolean;
    isModeResolved?: boolean;
    modeRequestVersion?: number;
    paperBalanceProp: number | null;
    paperRiskSettingsProp?: {
        investmentPercentage: number | null;
        leverage: number | null;
    };
    symbolProp?: string;
    setBatchData: Dispatch<SetStateAction<BatchResponseData | undefined>>;
    onLoaded?: () => void;
    onError?: (error: string) => void;
    onWebSocketBalanceUpdate?: (handler: (event: BalanceUpdateEvent) => void) => () => void;
    onWebSocketPositionUpdate?: (handler: (event: PositionUpdateEvent) => void) => () => void;
    onStatsUpdaterRegister?: (updaterSetter: (updater: (prev: any) => any) => void) => () => void;
    useLoadingConsolidation?: boolean;
    onResolvedLiveBalanceChange?: (value: number | null) => void;
}

function DashboardTopGridComponent({
    isPaperModeProp = false,
    isModeResolved = true,
    modeRequestVersion = 0,
    paperBalanceProp = null,
    paperRiskSettingsProp,
    symbolProp = DEFAULT_SYMBOL,
    setBatchData,
    onLoaded = () => {},
    onError = () => {},
    onWebSocketBalanceUpdate,
    onWebSocketPositionUpdate,
    onStatsUpdaterRegister,
    onResolvedLiveBalanceChange,
}: DashboardTopGridProps) {
    const [stats, setStats] = useState<any>(null);
    const [balance, setBalance] = useState<string | null>(null);
    const [isBalanceUnavailable, setIsBalanceUnavailable] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const [dataValidated, setDataValidated] = useState(false);
    const [showRefreshButton, setShowRefreshButton] = useState(false);
    const [initialPaperBalance, setInitialPaperBalance] = useState<number | null>(null);

    // ✅ MOVE THESE HOOKS BEFORE THE REFS
    const { t } = useTranslation();
    const { isMobile } = useMobile();
    const { withLoadingConsolidation } = useLoadingConsolidation();

    // Use global NFT verification context
    const { isVerifying, tierStatus, refreshTierStatus } = useNFTVerificationContext();

    const isPaperMode = isPaperModeProp;
    const paperBalance = paperBalanceProp;

    // ✅ NOW refs can safely use isPaperMode and paperBalance
    const isPaperModeRef = useRef(isPaperMode);

    // Ref to access current paperBalance without dependency
    const paperBalanceRef = useRef(paperBalance);
    const batchRequestRef = useRef<Promise<BatchResponseData> | null>(null);
    const setBatchDataRef = useRef(setBatchData);
    const onLoadedRef = useRef(onLoaded);
    const onErrorRef = useRef(onError);
    const refreshTierStatusRef = useRef(refreshTierStatus);
    const lastWsBalanceRef = useRef<string | null>(null);
    const lastResolvedBalanceRef = useRef<string | null>(null);
    const balanceAbortControllerRef = useRef<AbortController | null>(null);
    const balanceRequestIdRef = useRef(0);
    const missingCredentialsRef = useRef(false);
    const isMountedRef = useRef(true);
    const latestModeRequestVersionRef = useRef(modeRequestVersion);
    const bootstrapRequestKeyRef = useRef<string | null>(null);

    const formatBalanceValue = useCallback(
        (value: number) =>
            `$${value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 8,
            })}`,
        [],
    );

    const publishResolvedLiveBalance = useCallback(
        (value: number | null) => {
            onResolvedLiveBalanceChange?.(
                typeof value === 'number' && Number.isFinite(value) ? value : null,
            );
        },
        [onResolvedLiveBalanceChange],
    );
    // Synchronize isPaperModeRef whenever isPaperMode changes
    useEffect(() => {
        isPaperModeRef.current = isPaperMode;
    }, [isPaperMode]);

    useEffect(() => {
        latestModeRequestVersionRef.current = modeRequestVersion;
    }, [modeRequestVersion]);

    // Synchronize paperBalanceRef whenever paperBalance changes
    useEffect(() => {
        paperBalanceRef.current = paperBalance;
    }, [paperBalance]);

    useEffect(() => {
        setBatchDataRef.current = setBatchData;
    }, [setBatchData]);

    useEffect(() => {
        onLoadedRef.current = onLoaded;
    }, [onLoaded]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        refreshTierStatusRef.current = refreshTierStatus;
    }, [refreshTierStatus]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            balanceAbortControllerRef.current?.abort();
            balanceAbortControllerRef.current = null;
        };
    }, []);

    // Allow parent dashboard to update stats via registered updater
    useEffect(() => {
        if (!onStatsUpdaterRegister) {
            return;
        }

        const unregister = onStatsUpdaterRegister(updater => {
            setStats((prevStats: any) => updater(prevStats));
        });

        return () => {
            unregister?.();
        };
    }, [onStatsUpdaterRegister]);

    // Register balance update handler with parent
    useEffect(() => {
        if (!onWebSocketBalanceUpdate) {
            return;
        }

        const unregister = onWebSocketBalanceUpdate(event => {
            const balanceValue = event.balance?.balance;

            if (balanceValue != null && Number.isFinite(Number(balanceValue))) {
                const numericBalance = Number(balanceValue);
                const formattedBalance = formatBalanceValue(numericBalance);
                lastWsBalanceRef.current = formattedBalance;
                lastResolvedBalanceRef.current = formattedBalance;
                setIsBalanceUnavailable(false);
                setBalance(formattedBalance);
                publishResolvedLiveBalance(numericBalance);
            }
        });

        return () => {
            unregister?.();
        };
    }, [formatBalanceValue, onWebSocketBalanceUpdate, publishResolvedLiveBalance]);

    // Register position update handler with parent
    useEffect(() => {
        if (!onWebSocketPositionUpdate) {
            return;
        }

        const unregister = onWebSocketPositionUpdate(event => {
            const totalPnL = Number(event.total_pnl ?? event.metadata?.total_pnl);

            if (!Number.isFinite(totalPnL)) {
                return;
            }

            setStats((prevStats: any) =>
                prevStats
                    ? updateStatsFromRealtime(prevStats, totalPnL, {
                          mode: isPaperModeRef.current ? 'paper' : 'live',
                          useGuards: USE_WEBSOCKET_PNL_GUARDS,
                      })
                    : prevStats,
            );
        });

        return () => {
            unregister?.();
        };
    }, [onWebSocketPositionUpdate]);

    useEffect(() => {
        if (!isPaperModeRef.current) {
            try {
                websocketService.requestUpdate();
            } catch (_e) {
                // no-op
            }
        }
    }, []);

    const normalizeLiveStats = useCallback((data: any) => {
        if (!data || typeof data !== 'object') {
            return data;
        }

        const normalized = { ...data };
        const coerceNumber = (value: any): number | null => {
            if (value === null || value === undefined || value === '') {
                return null;
            }
            const numeric = Number(value);
            return Number.isFinite(numeric) ? numeric : null;
        };

        const resolvedTotalPnl = coerceNumber(
            data.total_pnl ?? data.total_pnl_value ?? data.pnl_value ?? data.pnl,
        );
        if (resolvedTotalPnl !== null) {
            normalized.total_pnl = resolvedTotalPnl;
            normalized.total_pnl_value = resolvedTotalPnl;
            normalized.pnl_value = resolvedTotalPnl;
            normalized.pnl = resolvedTotalPnl;
        }

        const resolvedMonthlyRate = coerceNumber(data.monthly_rate ?? data.monthlyRate);
        if (resolvedMonthlyRate !== null) {
            normalized.monthly_rate = resolvedMonthlyRate;
            normalized.monthlyRate = resolvedMonthlyRate;
        }

        // Server-computed calendar-month analytics (batch statistics section)
        const resolvedCurrentMonthPct = coerceNumber(data.current_month_pnl_pct);
        if (resolvedCurrentMonthPct !== null) {
            normalized.current_month_pnl_pct = resolvedCurrentMonthPct;
            if (resolvedMonthlyRate === null) {
                normalized.monthly_rate = resolvedCurrentMonthPct;
                normalized.monthlyRate = resolvedCurrentMonthPct;
            }
        }
        const resolvedAvgMonthPct = coerceNumber(data.avg_monthly_pnl_pct);
        if (resolvedAvgMonthPct !== null) {
            normalized.avg_monthly_pnl_pct = resolvedAvgMonthPct;
        }
        const resolvedCurrentMonthPnl = coerceNumber(data.current_month_pnl);
        if (resolvedCurrentMonthPnl !== null) {
            normalized.current_month_pnl = resolvedCurrentMonthPnl;
        }

        const resolvedWinRate = coerceNumber(data.win_rate ?? data.winRate);
        if (resolvedWinRate !== null) {
            normalized.win_rate = resolvedWinRate;
            normalized.winRate = resolvedWinRate;
        }

        return normalized;
    }, []);

    // Data validation helper function
    const validateDashboardData = useCallback(
        (data: any, mode: 'paper' | 'live'): { isValid: boolean; reason: string } => {
            if (!data) {
                return { isValid: false, reason: 'Data is null or undefined' };
            }

            if (mode === 'paper') {
                if (typeof data !== 'object') {
                    return { isValid: false, reason: 'Paper stats data is not an object' };
                }

                const requiredFields = ['total_pnl', 'win_rate', 'total_trades'] as const;
                const missingField = requiredFields.find(
                    field => (data as Record<string, unknown>)[field] === undefined,
                );
                if (missingField) {
                    return {
                        isValid: false,
                        reason: `Missing ${missingField} field in paper stats`,
                    };
                }

                return { isValid: true, reason: 'Valid paper trading data' };
            } else {
                // For live mode, check if data has expected properties and valid numeric types
                if (typeof data !== 'object') {
                    return { isValid: false, reason: 'Data is not an object' };
                }
                // Check for minimal required fields: PnL, a monthly figure, and win_rate
                const hasPnl =
                    'pnl' in data ||
                    'total_pnl_value' in data ||
                    'pnl_value' in data ||
                    'total_pnl' in data;
                const hasMonthlyRate =
                    'monthly_rate' in data ||
                    'monthlyRate' in data ||
                    'current_month_pnl_pct' in data ||
                    'monthly_avg_pnl_per_trade' in data;
                const hasWinRate = 'win_rate' in data || 'winRate' in data;

                if (!hasPnl || !hasMonthlyRate || !hasWinRate) {
                    return {
                        isValid: false,
                        reason: 'Missing required fields: need PnL (pnl/total_pnl_value/pnl_value), a monthly figure (monthly_rate/current_month_pnl_pct), and win_rate',
                    };
                }
                // Validate numeric types for key fields when present (null = not computable, allowed)
                const numericFields = [
                    'pnl',
                    'pnl_today',
                    'monthly_rate',
                    'monthlyRate',
                    'win_rate',
                    'winRate',
                    'total_pnl_value',
                    'pnl_value',
                    'total_pnl',
                    'current_month_pnl_pct',
                    'avg_monthly_pnl_pct',
                    'current_month_pnl',
                    'monthly_avg_pnl_per_trade',
                ];
                for (const field of numericFields) {
                    if (field in data && data[field] !== null) {
                        const value = data[field];
                        if (typeof value !== 'number' || isNaN(value)) {
                            return {
                                isValid: false,
                                reason: `Field '${field}' is not a valid number`,
                            };
                        }
                    }
                }
                return { isValid: true, reason: 'Valid live trading data' };
            }
        },
        [],
    );

    // Helper function to add timeout to async API calls
    const fetchWithTimeout = useCallback(
        async <T,>(fn: () => Promise<T>, timeoutMs: number = 10000): Promise<T> => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

            try {
                const result = await fn();
                clearTimeout(timeoutId);
                return result;
            } catch (error: any) {
                clearTimeout(timeoutId);
                if (error.name === 'AbortError' || controller.signal.aborted) {
                    throw new Error(`Request timed out after ${timeoutMs}ms`);
                }
                throw error;
            }
        },
        [],
    );

    // Retry helper function with exponential backoff
    // maxRetries = number of retries after initial attempt (total attempts = maxRetries + 1)
    const retryWithBackoff = useCallback(
        async <T,>(
            fetchFunction: () => Promise<T>,
            maxRetries: number = 3,
            baseDelay: number = 100,
        ): Promise<T> => {
            let lastError: any;
            const totalAttempts = maxRetries + 1; // Initial attempt + retries

            for (let attempt = 0; attempt < totalAttempts; attempt++) {
                try {
                    if (attempt > 0) {
                        const delay = baseDelay * Math.pow(3, attempt - 1);
                        console.log(
                            `[DashboardTopGrid] Retry ${attempt}/${maxRetries}, waiting ${delay}ms...`,
                        );
                        setRetryCount(attempt);
                        setIsRetrying(true);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }

                    const result = await fetchWithTimeout(fetchFunction, 10000); // 10 second timeout

                    if (attempt > 0) {
                        console.log(`[DashboardTopGrid] Retry ${attempt}/${maxRetries} succeeded`);
                        setIsRetrying(false);
                    }

                    return result;
                } catch (error) {
                    lastError = error;
                    console.error(
                        `[DashboardTopGrid] Attempt ${attempt + 1}/${totalAttempts} failed:`,
                        error,
                    );

                    if (attempt === totalAttempts - 1) {
                        setIsRetrying(false);
                        throw lastError;
                    }
                }
            }

            throw lastError;
        },
        [fetchWithTimeout],
    );

    // Function to refresh dashboard data
    // useRetries: if true, uses retry logic with exponential backoff (for initial load)
    //             if false, single fetch attempt (for periodic refreshes)
    const requestBatchData = useCallback(
        (
            forceRefresh: boolean = false,
            include: readonly string[] = PRIMARY_BATCH_SECTIONS,
        ): Promise<BatchResponseData> => {
            const modeKey = isPaperMode ? 'paper' : 'live';
            if (!forceRefresh && batchRequestRef.current) {
                return batchRequestRef.current;
            }

            const componentId = `dashboardBatch:${modeKey}:${symbolProp}:${include.join(',')}${forceRefresh ? ':fresh' : ''}`;
            const fn = () =>
                getDashboardBatch(symbolProp, undefined, { forceRefresh, include: [...include] });
            const requestPromise = withLoadingConsolidation(componentId, fn);
            batchRequestRef.current = requestPromise.finally(() => {
                batchRequestRef.current = null;
            });
            return batchRequestRef.current;
        },
        [isPaperMode, symbolProp, withLoadingConsolidation],
    );

    const isStaleModeRequest = useCallback(
        (requestVersion: number, requestMode: boolean) =>
            !isMountedRef.current ||
            latestModeRequestVersionRef.current !== requestVersion ||
            isPaperModeRef.current !== requestMode,
        [],
    );

    // Helper to resolve live balance with fallbacks
    const resolveLiveBalance = useCallback(
        async (
            rawBalance: unknown,
            options?: {
                allowNetworkFallback?: boolean;
                forceRefresh?: boolean;
                signal?: AbortSignal;
            },
        ): Promise<{ display: string | null; unavailable: boolean }> => {
            const allowNetworkFallback = options?.allowNetworkFallback ?? false;
            missingCredentialsRef.current = false;

            if (rawBalance !== null && rawBalance !== undefined) {
                const numericValue = Number(rawBalance);
                if (Number.isFinite(numericValue)) {
                    missingCredentialsRef.current = false;
                    publishResolvedLiveBalance(numericValue);
                    return {
                        display: formatBalanceValue(numericValue),
                        unavailable: false,
                    };
                }
            }

            if (allowNetworkFallback) {
                try {
                    const realBalanceResponse: any = await getRealBalance({
                        forceRefresh: Boolean(options?.forceRefresh),
                        fast: true,
                        signal: options?.signal,
                        debounce: 0,
                    });
                    const fallbackValue =
                        realBalanceResponse && typeof realBalanceResponse === 'object'
                            ? (realBalanceResponse.balance ??
                              realBalanceResponse.data?.balance ??
                              null)
                            : realBalanceResponse;
                    const isStale = Boolean(
                        realBalanceResponse &&
                        typeof realBalanceResponse === 'object' &&
                        realBalanceResponse.is_stale,
                    );

                    if (!isStale && fallbackValue !== null && fallbackValue !== undefined) {
                        const fallbackNumeric = Number(fallbackValue);
                        if (Number.isFinite(fallbackNumeric)) {
                            console.debug(
                                '[DashboardTopGrid] Using fallback real balance API response.',
                            );
                            missingCredentialsRef.current = false;
                            publishResolvedLiveBalance(fallbackNumeric);
                            return {
                                display: formatBalanceValue(fallbackNumeric),
                                unavailable: false,
                            };
                        }
                    }

                    if (isStale) {
                        console.info(
                            '[DashboardTopGrid] Real balance endpoint returned stale data; rendering unavailable state.',
                        );
                        publishResolvedLiveBalance(null);
                        return {
                            display: null,
                            unavailable: true,
                        };
                    }
                } catch (fallbackError: any) {
                    const errorMessage = String(
                        fallbackError?.responseData?.error || fallbackError?.message || '',
                    ).toLowerCase();
                    const missingCredentials =
                        fallbackError?.status === 400 &&
                        errorMessage.includes('no active api credential');

                    if (missingCredentials) {
                        console.info(
                            '[DashboardTopGrid] Real balance unavailable because no API credentials are connected.',
                        );
                        lastResolvedBalanceRef.current = null;
                        lastWsBalanceRef.current = null;
                        missingCredentialsRef.current = true;
                        publishResolvedLiveBalance(null);
                        return {
                            display: null,
                            unavailable: true,
                        };
                    }

                    console.warn(
                        '[DashboardTopGrid] Failed to fetch fallback real balance:',
                        fallbackError,
                    );
                }
            }

            publishResolvedLiveBalance(null);
            return {
                display: null,
                unavailable: true,
            };
        },
        [formatBalanceValue, publishResolvedLiveBalance],
    );

    const refreshLiveBalance = useCallback(
        (
            rawBalance: unknown,
            forceRefresh: boolean,
            requestVersion: number,
            requestMode: boolean,
        ) => {
            const requestId = ++balanceRequestIdRef.current;
            balanceAbortControllerRef.current?.abort();

            const controller = new AbortController();
            balanceAbortControllerRef.current = controller;

            void resolveLiveBalance(rawBalance, {
                allowNetworkFallback: true,
                forceRefresh,
                signal: controller.signal,
            })
                .then(resolvedBalance => {
                    if (
                        requestId !== balanceRequestIdRef.current ||
                        isStaleModeRequest(requestVersion, requestMode)
                    ) {
                        return;
                    }

                    if (!resolvedBalance.unavailable && resolvedBalance.display) {
                        lastResolvedBalanceRef.current = resolvedBalance.display;
                        lastWsBalanceRef.current = resolvedBalance.display;
                        setIsBalanceUnavailable(false);
                        setBalance(resolvedBalance.display);
                        return;
                    }

                    setIsBalanceUnavailable(true);
                    setBalance(null);
                })
                .catch((balanceError: any) => {
                    if (
                        balanceError?.name === 'AbortError' ||
                        requestId !== balanceRequestIdRef.current ||
                        isStaleModeRequest(requestVersion, requestMode)
                    ) {
                        return;
                    }

                    console.error('[DashboardTopGrid] Error resolving live balance:', balanceError);
                    setIsBalanceUnavailable(true);
                    setBalance(null);
                })
                .finally(() => {
                    if (balanceAbortControllerRef.current === controller) {
                        balanceAbortControllerRef.current = null;
                    }
                });
        },
        [isStaleModeRequest, resolveLiveBalance],
    );

    const refreshDashboardData = useCallback(
        async (useRetries: boolean = true, forceNetwork: boolean = false) => {
            if (!isModeResolved) {
                return;
            }

            const requestVersion = latestModeRequestVersionRef.current;
            const requestMode = isPaperMode;

            try {
                const shouldForceNetwork = forceNetwork || useRetries;
                if (requestMode) {
                    // For paper mode, use the optimized batch endpoint
                    console.log(
                        '[DashboardTopGrid] Fetching paper trading data via batch endpoint...',
                    );
                    try {
                        const batchData = useRetries
                            ? await retryWithBackoff(() => requestBatchData(true), 3, 100)
                            : await requestBatchData(shouldForceNetwork);
                        if (isStaleModeRequest(requestVersion, requestMode)) {
                            return;
                        }
                        setBatchDataRef.current(prev => mergeDashboardBatchData(prev, batchData));
                        console.log('[DashboardTopGrid] Paper batch data received:', batchData);

                        // Define interface for statistics data
                        interface StatisticsData {
                            total_pnl?: number;
                            win_rate?: number;
                            total_trades?: number;
                            monthly_avg_pnl_per_trade?: number;
                            current_month_pnl?: number;
                            current_month_pnl_pct?: number | null;
                            avg_monthly_pnl_pct?: number | null;
                        }

                        // Extract statistics from batch response
                        const paperStats: StatisticsData = batchData.statistics || {};

                        // Validate the fetched data
                        const validation = validateDashboardData(paperStats, 'paper');
                        console.log(
                            `[DashboardTopGrid] Data validation ${validation.isValid ? 'passed' : 'failed'}: ${validation.reason}`,
                        );

                        const apiIndicatesFailure = !!batchData.statistics?.error;
                        if (validation.isValid && !apiIndicatesFailure) {
                            // Server-computed calendar-month analytics (realized PnL basis)
                            const currentMonthPct = Number(paperStats.current_month_pnl_pct);
                            const avgMonthPct = Number(paperStats.avg_monthly_pnl_pct);
                            const monthlyRate = Number.isFinite(currentMonthPct)
                                ? currentMonthPct
                                : 0;

                            const formattedStats = {
                                total_pnl: paperStats.total_pnl || 0,
                                pnl: paperStats.total_pnl || 0,
                                pnl_today: 0, // Could be enhanced to calculate today's PnL
                                monthly_rate: monthlyRate,
                                monthlyRate: monthlyRate,
                                current_month_pnl_pct: Number.isFinite(currentMonthPct)
                                    ? currentMonthPct
                                    : null,
                                avg_monthly_pnl_pct: Number.isFinite(avgMonthPct)
                                    ? avgMonthPct
                                    : null,
                                current_month_pnl: Number(paperStats.current_month_pnl || 0),
                                win_rate: paperStats.win_rate || 0,
                                winRate: paperStats.win_rate || 0,
                                total_trades: paperStats.total_trades || 0,
                            };

                            console.log(
                                '[DashboardTopGrid] ===== PAPER TRADING STATISTICS SUMMARY =====',
                            );
                            console.log(
                                `[DashboardTopGrid] Total Trades: ${paperStats.total_trades || 0}`,
                            );
                            console.log(
                                `[DashboardTopGrid] Total P&L: $${(paperStats.total_pnl || 0).toFixed(2)}`,
                            );
                            console.log(
                                `[DashboardTopGrid] Win Rate: ${(paperStats.win_rate || 0).toFixed(1)}%`,
                            );
                            console.log(
                                `[DashboardTopGrid] Monthly Rate: ${monthlyRate.toFixed(2)}%`,
                            );
                            console.log(
                                '[DashboardTopGrid] ===============================================',
                            );
                            console.log('[DashboardTopGrid] Setting stats:', formattedStats);
                            setStats(formattedStats);
                            setDataValidated(true);
                            setShowRefreshButton(false);
                        } else {
                            console.warn(
                                `[DashboardTopGrid] Paper trading statistics validation failed after retries: ${validation.reason}`,
                            );
                            // Don't set fallback stats - keep stats as null to preserve skeleton
                            setDataValidated(false);
                            setShowRefreshButton(true);
                            console.error(
                                '[DashboardTopGrid] Failed to load dashboard data after 3 retries',
                            );
                        }
                    } catch (paperError: any) {
                        console.error(
                            '[DashboardTopGrid] Error fetching paper trading batch data after retries:',
                            paperError,
                        );
                        // Log detailed error information
                        if (paperError?.response) {
                            console.error('[DashboardTopGrid] API Response Error:', {
                                status: paperError.response.status,
                                statusText: paperError.response.statusText,
                                data: paperError.response.data,
                            });
                        } else if (paperError?.message) {
                            console.error('[DashboardTopGrid] Error message:', paperError.message);
                        } else if (paperError?.status) {
                            console.error('[DashboardTopGrid] Fetch Error:', {
                                status: paperError.status,
                                statusText: paperError.statusText,
                            });
                        }

                        // Set fallback data for debugging
                        console.log(
                            '[DashboardTopGrid] Setting fallback paper trading data for debugging',
                        );
                        const fallbackStats = {
                            total_pnl: 0,
                            pnl: 0,
                            pnl_today: 0,
                            monthly_rate: 0,
                            monthlyRate: 0,
                            current_month_pnl_pct: null,
                            avg_monthly_pnl_pct: null,
                            current_month_pnl: 0,
                            win_rate: 0,
                            winRate: 0,
                            total_trades: 0,
                            _isFallback: true,
                        };
                        setStats(fallbackStats);
                        setDataValidated(true);
                        setShowRefreshButton(true); // Still show refresh button since data is fallback
                    }
                } else {
                    // Fetch live trading data via batch endpoint with optional retry logic
                    try {
                        const batchData = useRetries
                            ? await retryWithBackoff(() => requestBatchData(true), 3, 100)
                            : await requestBatchData(shouldForceNetwork);
                        if (isStaleModeRequest(requestVersion, requestMode)) {
                            return;
                        }
                        setBatchDataRef.current(prev => mergeDashboardBatchData(prev, batchData));

                        // Extract statistics and balance from batch response
                        const liveStats = normalizeLiveStats(batchData.statistics);

                        // Validate the fetched data
                        const validation = validateDashboardData(liveStats, 'live');
                        console.log(
                            `[DashboardTopGrid] Data validation ${validation.isValid ? 'passed' : 'failed'}: ${validation.reason}`,
                        );

                        if (validation.isValid) {
                            setStats(liveStats);
                            setDataValidated(true);
                            setShowRefreshButton(false);
                        } else {
                            console.warn(
                                `[DashboardTopGrid] Live trading statistics validation failed: ${validation.reason}`,
                            );
                            setDataValidated(false);
                            setShowRefreshButton(true);
                        }

                        refreshLiveBalance(
                            batchData?.balance?.balance,
                            shouldForceNetwork,
                            requestVersion,
                            requestMode,
                        );
                    } catch (liveError: any) {
                        console.error(
                            '[DashboardTopGrid] Error fetching live trading statistics after retries:',
                            liveError,
                        );
                        // Log detailed error information
                        if (liveError?.response) {
                            console.error('[DashboardTopGrid] API Response Error:', {
                                status: liveError.response.status,
                                statusText: liveError.response.statusText,
                                data: liveError.response.data,
                            });
                        } else if (liveError?.message) {
                            console.error('[DashboardTopGrid] Error message:', liveError.message);
                        } else if (liveError?.status) {
                            console.error('[DashboardTopGrid] Fetch Error:', {
                                status: liveError.status,
                                statusText: liveError.statusText,
                            });
                        }

                        // Set fallback data for debugging
                        console.log(
                            '[DashboardTopGrid] Setting fallback live trading data for debugging',
                        );
                        const fallbackStats = {
                            total_pnl: 0,
                            pnl: 0,
                            pnl_today: 0,
                            monthly_rate: 0,
                            monthlyRate: 0,
                            current_month_pnl_pct: null,
                            avg_monthly_pnl_pct: null,
                            current_month_pnl: 0,
                            win_rate: 0,
                            winRate: 0,
                            total_trades: 0,
                            _isFallback: true,
                        };
                        setStats(fallbackStats);
                        setDataValidated(true);
                        setShowRefreshButton(true); // Still show refresh button since data is fallback
                        refreshLiveBalance(
                            undefined,
                            shouldForceNetwork,
                            requestVersion,
                            requestMode,
                        );
                    } finally {
                        if (!isPaperModeRef.current) {
                            window.dispatchEvent(
                                new CustomEvent('profitShareSummaryRefresh', {
                                    detail: {
                                        source: 'dashboard_refresh',
                                        forced: shouldForceNetwork,
                                        timestamp: Date.now(),
                                    },
                                }),
                            );
                        }
                    }
                }

                if (isStaleModeRequest(requestVersion, requestMode)) {
                    return;
                }
                refreshTierStatusRef.current?.();
            } catch (error) {
                if (isStaleModeRequest(requestVersion, requestMode)) {
                    return;
                }
                const errorMessage =
                    error instanceof Error ? error.message : 'An unknown error occurred';
                console.error('[DashboardTopGrid] Error refreshing dashboard data:', error);
                setDataValidated(false);
                setShowRefreshButton(true);
                onErrorRef.current?.(errorMessage);
            }
        },
        [
            isModeResolved,
            isPaperMode,
            isStaleModeRequest,
            retryWithBackoff,
            validateDashboardData,
            requestBatchData,
            refreshLiveBalance,
            normalizeLiveStats,
        ],
    );

    // Watch for dataValidated changes and call onLoaded when data is validated
    useEffect(() => {
        if (dataValidated) {
            console.log('[DashboardTopGrid] Data validated and loaded successfully');
            onLoadedRef.current?.();
        } else if (showRefreshButton) {
            console.log('[DashboardTopGrid] Data validation failed, keeping skeleton visible');
        }
    }, [dataValidated, showRefreshButton]);

    // Fetch initial paper balance when in paper mode
    useEffect(() => {
        if (isModeResolved && isPaperMode && initialPaperBalance === null) {
            console.log('[DashboardTopGrid] Fetching initial paper balance...');
            paperTradingApi
                .getPaperAccount()
                .then(response => {
                    if (response.success && response.account) {
                        const initBalance = response.account.initial_balance;
                        console.log(
                            `[DashboardTopGrid] Initial paper balance fetched: ${initBalance}`,
                        );
                        setInitialPaperBalance(initBalance);
                    }
                })
                .catch(error => {
                    console.error(
                        '[DashboardTopGrid] Error fetching initial paper balance:',
                        error,
                    );
                    // Fallback to a default if fetch fails
                    setInitialPaperBalance(10000);
                });
        }
    }, [isModeResolved, isPaperMode, initialPaperBalance]);

    // (Removed) legacy recompute of "monthly rate" from paperBalance vs
    // initialPaperBalance: that value was a lifetime total return mislabeled
    // as monthly. The card now shows server-computed calendar-month analytics
    // from the batch statistics section (current month + monthly average).

    useEffect(() => {
        const bootstrapKey = isModeResolved
            ? `${modeRequestVersion}:${isPaperMode ? 'paper' : 'live'}`
            : 'unresolved';

        if (bootstrapRequestKeyRef.current === bootstrapKey) {
            return;
        }

        bootstrapRequestKeyRef.current = bootstrapKey;

        if (!isModeResolved) {
            setRetryCount(0);
            setDataValidated(false);
            setShowRefreshButton(false);
            setBalance(null);
            setIsBalanceUnavailable(false);
            return;
        }

        // Reset retry state before initial data fetch
        setRetryCount(0);
        setDataValidated(false);
        setShowRefreshButton(false);
        setBalance(null);
        setIsBalanceUnavailable(false);

        // Render the cards from the fastest available batch payload first, then
        // allow periodic refreshes to update them in the background.
        refreshDashboardData(false, false);

        // Set up periodic refresh for paper trading data (every 30 seconds)
        // Use single fetch without retries for periodic refreshes
        const analyticsRefreshInterval = setInterval(() => {
            if (isPaperModeRef.current) {
                refreshDashboardData(false);
            } else {
                refreshDashboardData(false, true);
            }
        }, 30000); // 30 seconds

        // Subscribe to real-time updates with proper authentication
        return () => {
            clearInterval(analyticsRefreshInterval);
        };
    }, [isModeResolved, isPaperMode, modeRequestVersion, refreshDashboardData]);

    useEffect(() => {
        const handleCredentialsUpdated = (event: Event) => {
            const detail = (event as CustomEvent)?.detail as
                { credentials?: Record<string, any> } | undefined;
            const hasActiveCredentials = detail?.credentials
                ? Object.values(detail.credentials).some(Boolean)
                : undefined;

            if (hasActiveCredentials === false) {
                missingCredentialsRef.current = true;
                lastResolvedBalanceRef.current = null;
                lastWsBalanceRef.current = null;
                publishResolvedLiveBalance(null);
                setStats(null);
                setBalance(null);
                setIsBalanceUnavailable(true);
                setDataValidated(false);
                setShowRefreshButton(false);
            } else if (hasActiveCredentials) {
                missingCredentialsRef.current = false;
                setIsBalanceUnavailable(false);
            }

            requestCache.invalidate('/api/dashboard/batch*');
            requestCache.invalidate('/api/trading/real-balance*');

            if (hasActiveCredentials === false) {
                return;
            }

            refreshDashboardData(false, true).catch(err => {
                console.warn('[DashboardTopGrid] Failed to refresh after credentials change', err);
            });
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('credentialsUpdated', handleCredentialsUpdated);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('credentialsUpdated', handleCredentialsUpdated);
            }
        };
    }, [publishResolvedLiveBalance, refreshDashboardData]);

    // Listen for paper trading reset events and refresh dashboard data
    useEffect(() => {
        const handlePaperTradingReset = () => {
            if (isPaperMode) {
                console.log('[DashboardTopGrid] Reset event detected, refreshing dashboard data');

                // Don't clear stats to zero - just refresh the data
                // The refreshDashboardData will fetch fresh stats from the API
                refreshDashboardData(true); // Reset event should use retries
            }
        };

        window.addEventListener('paperTradingReset', handlePaperTradingReset as EventListener);

        return () => {
            window.removeEventListener(
                'paperTradingReset',
                handlePaperTradingReset as EventListener,
            );
        };
    }, [isPaperMode, refreshDashboardData]);

    // Manual refresh handler
    const handleManualRefresh = useCallback(() => {
        console.log('[DashboardTopGrid] Manual refresh triggered');
        setRetryCount(0);
        setDataValidated(false);
        setShowRefreshButton(false);
        refreshDashboardData(true); // Manual refresh should use retries
    }, [refreshDashboardData]);

    return (
        <>
            {(isRetrying || showRefreshButton) && (
                <div className={`mb-3 flex ${isMobile ? 'justify-start' : 'justify-end'}`}>
                    {isRetrying ? (
                        <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md shadow-sm">
                            <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-blue-600"></div>
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                                Retrying... ({retryCount}/3)
                            </span>
                        </div>
                    ) : (
                        <button
                            onClick={handleManualRefresh}
                            className="flex items-center gap-2 px-3 py-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md shadow-sm hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                        >
                            <svg
                                className="w-3 h-3 text-orange-600 dark:text-orange-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                            <span className="text-xs text-orange-600 dark:text-orange-400">
                                Refresh
                            </span>
                        </button>
                    )}
                </div>
            )}
            <div
                className={`grid ${isMobile ? 'grid-cols-1 gap-3 mb-4 w-full min-w-0' : 'gap-4 md:grid-cols-2 lg:grid-cols-5 mb-6'} max-w-full`}
            >
                <Card>
                    <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                        <div className="flex items-center gap-1">
                            <h3
                                className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}
                            >
                                {isPaperMode ? t('trading.virtualBalance') : t('dashboard.balance')}
                            </h3>
                            <span
                                title={
                                    isPaperMode
                                        ? t('trading.tooltips.virtualBalance')
                                        : t('dashboard.tooltips.balance')
                                }
                            >
                                <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-500 cursor-help inline-block" />
                            </span>
                        </div>
                        <p
                            className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold ${isPaperMode ? 'text-orange-600 dark:text-orange-400' : ''} ${!isPaperMode && isBalanceUnavailable ? 'text-base md:text-lg text-muted-foreground' : ''}`}
                        >
                            {isPaperMode
                                ? `$${(paperBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : isBalanceUnavailable
                                  ? BALANCE_UNAVAILABLE_LABEL
                                  : (balance ?? '--')}
                        </p>
                        {isPaperMode && paperRiskSettingsProp?.investmentPercentage !== null && (
                            <div className="mt-2 text-xs text-orange-600 dark:text-orange-300 space-y-1">
                                <div>
                                    {t('dashboard.riskSettings.investmentPercentage')}:{' '}
                                    {formatNumber(
                                        paperRiskSettingsProp?.investmentPercentage ?? 0,
                                        Number.isInteger(
                                            paperRiskSettingsProp?.investmentPercentage ?? 0,
                                        )
                                            ? 0
                                            : 2,
                                    )}
                                    %
                                </div>
                                {paperRiskSettingsProp?.leverage !== null && (
                                    <div>
                                        {t('dashboard.riskSettings.leverage')}:{' '}
                                        {formatNumber(paperRiskSettingsProp?.leverage ?? 0, 2)}x
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                        <div className="flex items-center gap-1">
                            <h3
                                className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}
                            >
                                {isPaperMode ? 'Profit and Loss' : t('dashboard.pnlToday')}
                            </h3>
                            <span
                                title={
                                    isPaperMode
                                        ? t('trading.tooltips.profitAndLoss')
                                        : t('dashboard.tooltips.pnlToday')
                                }
                            >
                                <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-500 cursor-help inline-block" />
                            </span>
                        </div>
                        <p
                            className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold ${isPaperMode ? 'text-orange-600 dark:text-orange-400' : ''}`}
                        >
                            {(() => {
                                const pnlValue = isPaperMode
                                    ? (stats?.total_pnl ?? 0)
                                    : (stats?.pnl_today ??
                                      stats?.pnl ??
                                      stats?.total_pnl_value ??
                                      stats?.pnl_value ??
                                      0);
                                console.log(
                                    '[PnL Display] Realized P&L (completed trades):',
                                    pnlValue,
                                    'Stats:',
                                    stats,
                                );
                                const displayValue = stats
                                    ? `$${pnlValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : '--';
                                return stats?._isFallback ? `${displayValue} ⚠️` : displayValue;
                            })()}
                        </p>
                        {isPaperMode && (
                            <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">
                                {t('trading.paperTradingLabel')}
                            </p>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                        <div className="flex items-center gap-1">
                            <h3
                                className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}
                            >
                                {t('dashboard.currentMonthRate')}
                            </h3>
                            <span title={t('dashboard.tooltips.currentMonthRate')}>
                                <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-500 cursor-help inline-block" />
                            </span>
                        </div>
                        <p
                            className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold ${isPaperMode ? 'text-orange-600 dark:text-orange-400' : ''}`}
                        >
                            {(() => {
                                if (!stats) return '--';
                                const currentMonthPct = Number(
                                    stats.current_month_pnl_pct ??
                                        stats.monthly_rate ??
                                        stats.monthlyRate ??
                                        0,
                                );
                                const display = Number.isFinite(currentMonthPct)
                                    ? `${currentMonthPct.toFixed(2)}%`
                                    : '--';
                                return stats._isFallback ? `${display} ⚠️` : display;
                            })()}
                        </p>
                        {(() => {
                            const avgPct = Number(stats?.avg_monthly_pnl_pct);
                            if (!stats || !Number.isFinite(avgPct)) {
                                return null;
                            }
                            return (
                                <p className="text-xs text-muted-foreground mt-1">
                                    <span title={t('dashboard.tooltips.monthlyAvgRate')}>
                                        {t('dashboard.monthlyAvgLabel')}:{' '}
                                        <span className="font-semibold">{avgPct.toFixed(2)}%</span>
                                    </span>
                                </p>
                            );
                        })()}
                        {isPaperMode && (
                            <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">
                                {t('trading.paperTradingLabel')}
                            </p>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                        <div className="flex items-center gap-1">
                            <h3
                                className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}
                            >
                                {t('dashboard.winRate')}
                            </h3>
                            <span title={t('dashboard.tooltips.winRate')}>
                                <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-500 cursor-help inline-block" />
                            </span>
                        </div>
                        <p
                            className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold ${isPaperMode ? 'text-orange-600 dark:text-orange-400' : ''}`}
                        >
                            {stats
                                ? `${(stats.win_rate ?? stats.winRate ?? 0).toFixed(1)}%${stats._isFallback ? ' ⚠️' : ''}`
                                : '--'}
                        </p>
                        {isPaperMode && (
                            <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">
                                {t('trading.paperTradingLabel')}
                            </p>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
                        <h3
                            className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-muted-foreground`}
                        >
                            {t('dashboard.tier')}
                        </h3>
                        <div className="flex items-center gap-2">
                            <p
                                className={`${isMobile ? 'text-base' : 'text-lg'} font-bold ${isPaperMode ? 'text-orange-600 dark:text-orange-400' : ''}`}
                            >
                                {isPaperMode
                                    ? t('trading.paperModeTitle')
                                    : tierStatus
                                      ? tierStatus.tier_1
                                          ? t('tiers.tier1.name')
                                          : tierStatus.tier_2
                                            ? t('tiers.tier2.name')
                                            : tierStatus.tier_3
                                              ? t('tiers.tier3.name')
                                              : '--'
                                      : '--'}
                            </p>
                            {!isPaperMode && isVerifying && (
                                <div
                                    className={`animate-spin rounded-full ${isMobile ? 'h-3 w-3' : 'h-4 w-4'} border-t-2 border-b-2 border-primary`}
                                ></div>
                            )}
                        </div>

                        {/* Paper Mode Information */}
                        {isPaperMode ? (
                            <>
                                <p
                                    className={`${isMobile ? 'text-xs' : 'text-sm'} text-orange-500 dark:text-orange-400`}
                                >
                                    {t('trading.noProfitSharePaper')}
                                </p>
                                <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">
                                    {t('trading.paperTradingActive')}
                                </p>
                            </>
                        ) : (
                            /* Live Mode Information */
                            <>
                                <p
                                    className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}
                                >
                                    {tierStatus?.tier_1 && t('dashboard.profitShare40')}
                                    {tierStatus?.tier_2 && t('dashboard.profitShare20')}
                                    {tierStatus?.tier_3 && t('dashboard.profitShare10')}
                                    {!tierStatus?.tier_1 &&
                                        !tierStatus?.tier_2 &&
                                        !tierStatus?.tier_3 &&
                                        '--'}
                                </p>
                                {isVerifying && tierStatus?.tier_3 && (
                                    <p className="text-xs text-blue-600 mt-1">
                                        {t('dashboard.verifyingNFT')}
                                    </p>
                                )}
                                {tierStatus?.profit_share_owed !== undefined &&
                                    tierStatus.profit_share_owed > 0 && (
                                        <p className="text-xs text-red-600 mt-1">
                                            {t('dashboard.debt')}: $
                                            {Number(tierStatus.profit_share_owed).toFixed(2)}
                                        </p>
                                    )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
}

export const DashboardTopGrid = memo(DashboardTopGridComponent);

// ActivePosition component is now imported from '@/components/ActivePosition'
export { ActivePosition } from '@/components/ActivePosition';

// Profit Share Summary Component
export function ProfitShareSummary() {
    const { t } = useTranslation();
    const { isMobile } = useMobile();
    const { isPaperMode } = usePaperTrading();
    const [profitData, setProfitData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [missingCredentials, setMissingCredentials] = useState(false);
    const previousDataRef = useRef<any>(null);
    const refreshInFlightRef = useRef(false);
    const lastRefreshTsRef = useRef(0);
    const activeCredentialsRef = useRef<boolean | null>(null);
    const inferredNetDepositsRef = useRef(0);

    const adjustProfitAnalysisForDetectedDeposits = useCallback(
        async (summary: any) => {
            if (!summary?.profit_analysis || isPaperMode) {
                return summary;
            }

            const profitAnalysis = summary.profit_analysis;
            const currentBalance = Number(profitAnalysis.current_balance ?? 0);
            const initialBalance = Number(profitAnalysis.initial_balance ?? 0);

            if (!Number.isFinite(currentBalance) || !Number.isFinite(initialBalance)) {
                return summary;
            }

            let realizedPnl = 0;
            try {
                const batch = await getDashboardBatch(undefined, undefined, {
                    include: ['statistics'],
                });
                const statistics = batch?.statistics ?? {};
                const resolvedPnl = Number(
                    statistics.total_pnl ??
                        statistics.total_pnl_value ??
                        statistics.pnl ??
                        statistics.pnl_value ??
                        0,
                );
                if (Number.isFinite(resolvedPnl)) {
                    realizedPnl = resolvedPnl;
                }
            } catch (batchError) {
                console.warn(
                    '[ProfitShareSummary] Unable to infer realized PnL from dashboard batch, keeping existing profit values.',
                    batchError,
                );
            }

            const rawNetChange = currentBalance - initialBalance;
            const inferredNetDeposits = Math.max(0, rawNetChange - realizedPnl);
            const adjustedTradingProfit = rawNetChange - inferredNetDeposits;

            if (inferredNetDeposits > inferredNetDepositsRef.current + 0.000001) {
                console.info(
                    '[ProfitShareSummary] Deposit event detected and excluded from profit share calculations.',
                    {
                        previousInferredDeposits: inferredNetDepositsRef.current,
                        inferredNetDeposits,
                        depositDelta: inferredNetDeposits - inferredNetDepositsRef.current,
                    },
                );
                inferredNetDepositsRef.current = inferredNetDeposits;
            }

            return {
                ...summary,
                profit_analysis: {
                    ...profitAnalysis,
                    net_deposits: inferredNetDeposits,
                    trading_profit: adjustedTradingProfit,
                    true_profit: adjustedTradingProfit,
                    profit_percentage:
                        initialBalance > 0 ? (adjustedTradingProfit / initialBalance) * 100 : 0,
                    is_profitable: adjustedTradingProfit > 0,
                },
            };
        },
        [isPaperMode],
    );

    const extractCredentialList = useCallback((payload: any): any[] => {
        if (!payload) {
            return [];
        }
        if (Array.isArray(payload)) {
            return payload;
        }
        if (Array.isArray(payload?.credentials)) {
            return payload.credentials;
        }
        if (typeof payload === 'object') {
            return Object.values(payload).filter(Boolean);
        }
        return [];
    }, []);

    const determineHasActiveCredentials = useCallback(async (): Promise<boolean | null> => {
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/trading/credentials`, {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                return null;
            }

            const data = await response.json();
            const list = extractCredentialList(data);
            const hasActive = list.some(
                (cred: any) => !!cred && (cred.is_active === undefined ? true : cred.is_active),
            );
            return hasActive;
        } catch (error) {
            if (!isAuthExpiredError(error)) {
                console.warn('[ProfitShareSummary] Unable to determine credential status:', error);
            }
            return null;
        }
    }, [extractCredentialList]);

    const ensureActiveCredentials = useCallback(
        async (explicit?: boolean): Promise<boolean | null> => {
            if (typeof explicit === 'boolean') {
                activeCredentialsRef.current = explicit;
                return explicit;
            }

            if (typeof activeCredentialsRef.current === 'boolean') {
                return activeCredentialsRef.current;
            }

            const latest = await determineHasActiveCredentials();
            if (typeof latest === 'boolean') {
                activeCredentialsRef.current = latest;
            }
            return latest;
        },
        [determineHasActiveCredentials],
    );

    useEffect(() => {
        const controller = new AbortController();

        const fetchProfitShareSummary = async (options?: {
            abortSignal?: AbortSignal;
            useCacheOnly?: boolean;
            hasActiveCredentials?: boolean;
        }) => {
            if (refreshInFlightRef.current) {
                return;
            }

            refreshInFlightRef.current = true;
            try {
                const credentialState = await ensureActiveCredentials(
                    options?.hasActiveCredentials,
                );
                if (credentialState === false) {
                    previousDataRef.current = null;
                    setProfitData(null);
                    setMissingCredentials(true);
                    setLoading(false);
                    refreshInFlightRef.current = false;
                    return;
                }

                const endpoint = options?.useCacheOnly
                    ? `${API_BASE_URL}/api/trading/profit-share/summary`
                    : `${API_BASE_URL}/api/trading/profit-share/summary?force_refresh=1`;
                const response = await authenticatedFetch(endpoint, {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    signal: options?.abortSignal,
                });

                if (response.ok) {
                    const data = await response.json();
                    const adjustedData = await adjustProfitAnalysisForDetectedDeposits(data);
                    previousDataRef.current = adjustedData;
                    setProfitData(adjustedData);
                    setMissingCredentials(false);
                    setLoading(false);
                    lastRefreshTsRef.current = Date.now();
                    activeCredentialsRef.current = true;
                } else {
                    let errorPayload: any = null;
                    try {
                        errorPayload = await response.json();
                    } catch (_e) {
                        // ignore non-JSON errors
                    }

                    const errorMessage = String(
                        errorPayload?.error || errorPayload?.message || '',
                    ).toLowerCase();
                    const noCredentials =
                        response.status === 400 &&
                        errorMessage.includes('no active api credential');

                    if (noCredentials) {
                        console.info(
                            '[ProfitShareSummary] No active API credentials - clearing cached profit share data.',
                        );
                        previousDataRef.current = null;
                        setProfitData(null);
                        setMissingCredentials(true);
                        setLoading(false);
                        activeCredentialsRef.current = false;
                        return;
                    }

                    console.error('Failed to fetch profit share summary');
                    setLoading(false);
                }
            } catch (error) {
                if ((error as Error)?.name !== 'AbortError' && !isAuthExpiredError(error)) {
                    console.error('Error fetching profit share summary:', error);
                }
                if (!previousDataRef.current) {
                    setLoading(false);
                }
            } finally {
                refreshInFlightRef.current = false;
            }
        };

        const handleExternalRefresh = (event: Event) => {
            const detail = (event as CustomEvent)?.detail as
                | { forced?: boolean; timestamp?: number; credentials?: Record<string, any> }
                | undefined;
            const hasActiveCredentials = detail?.credentials
                ? Object.values(detail.credentials).some(Boolean)
                : undefined;

            if (hasActiveCredentials === false) {
                console.info(
                    '[ProfitShareSummary] credentialsUpdated -> no connected exchanges, clearing summary UI',
                );
                previousDataRef.current = null;
                setProfitData(null);
                setMissingCredentials(true);
                setLoading(false);
                activeCredentialsRef.current = false;
                return;
            }

            if (hasActiveCredentials) {
                setMissingCredentials(false);
                activeCredentialsRef.current = true;
            }

            const isForced = detail?.forced ?? false;
            const lastRefresh = lastRefreshTsRef.current;
            const now = Date.now();

            if (!isForced && now - lastRefresh < 5000) {
                return;
            }

            if (!previousDataRef.current) {
                setLoading(true);
            }
            fetchProfitShareSummary({
                abortSignal: controller.signal,
                hasActiveCredentials,
                useCacheOnly: !isForced,
            }).catch(() => {});
        };

        fetchProfitShareSummary({ abortSignal: controller.signal, useCacheOnly: true }).catch(
            () => {},
        );
        window.addEventListener('profitShareSummaryRefresh', handleExternalRefresh);
        window.addEventListener('credentialsUpdated', handleExternalRefresh);

        return () => {
            controller.abort();
            window.removeEventListener('profitShareSummaryRefresh', handleExternalRefresh);
            window.removeEventListener('credentialsUpdated', handleExternalRefresh);
        };
    }, [adjustProfitAnalysisForDetectedDeposits, ensureActiveCredentials]);

    useEffect(() => {
        if (profitData) {
            previousDataRef.current = profitData;
        }
    }, [profitData]);

    if (missingCredentials) {
        return (
            <Card className="mb-6">
                <CardContent className="p-6 space-y-3">
                    <h3 className="text-lg font-semibold">{t('dashboard.profitShare.title')}</h3>
                    <div className="text-muted-foreground">
                        <p className="font-medium">
                            {t('notifications.autoTrading.missingCredentialsTitle')}
                        </p>
                        <p className="text-sm">
                            {t('notifications.autoTrading.missingCredentialsDescription')}
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (loading && !previousDataRef.current) {
        return (
            <Card className="mb-6">
                <CardContent className="p-6">
                    <div className="text-center">{t('dashboard.profitShare.loading')}</div>
                </CardContent>
            </Card>
        );
    }

    const dataToRender = profitData || previousDataRef.current;

    if (!dataToRender) {
        return (
            <Card className="mb-6">
                <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">
                        {t('dashboard.profitShare.title')}
                    </h3>
                    <div className="text-center text-muted-foreground">
                        {t('dashboard.profitShare.noData')}
                    </div>
                </CardContent>
            </Card>
        );
    }

    const { tier_info, profit_analysis } = dataToRender;

    return (
        <Card className={`${isMobile ? 'mb-4 w-full min-w-0' : 'mb-6'} max-w-full overflow-hidden`}>
            <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
                <h3
                    className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold ${isMobile ? 'mb-3' : 'mb-4'}`}
                >
                    {t('dashboard.profitShare.title')}
                </h3>

                <div
                    className={`grid grid-cols-1 ${isMobile ? 'gap-3 mb-4' : 'md:grid-cols-3 gap-4 mb-6'}`}
                >
                    {/* Balance Overview */}
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <h4 className="font-medium text-sm text-muted-foreground mb-2">
                            {t('dashboard.profitShare.balanceOverview')}
                        </h4>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.initialBalance')}
                                </span>
                                <span className="font-medium">
                                    ${(profit_analysis?.initial_balance ?? 0).toFixed(6)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.currentBalance')}
                                </span>
                                <span className="font-medium">
                                    ${(profit_analysis?.current_balance ?? 0).toFixed(6)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.netDeposits')}
                                </span>
                                <span className="font-medium">
                                    ${(profit_analysis?.net_deposits ?? 0).toFixed(6)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Profit Analysis */}
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <h4 className="font-medium text-sm text-muted-foreground mb-2">
                            {t('dashboard.profitShare.profitAnalysis')}
                        </h4>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.tradingProfit')}
                                </span>
                                <span
                                    className={`font-medium ${(profit_analysis?.trading_profit ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                >
                                    ${(profit_analysis?.trading_profit ?? 0).toFixed(6)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.trueProfit')}
                                </span>
                                <span
                                    className={`font-medium ${(profit_analysis?.true_profit ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                >
                                    ${(profit_analysis?.true_profit ?? 0).toFixed(6)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.profitPercentage')}
                                </span>
                                <span
                                    className={`font-medium ${(profit_analysis?.profit_percentage ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                >
                                    {(profit_analysis?.profit_percentage ?? 0).toFixed(2)}%
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Profit Share Info */}
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <h4 className="font-medium text-sm text-muted-foreground mb-2">
                            {t('dashboard.profitShare.profitShareInfo')}
                        </h4>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.tierRate', {
                                        tier: tier_info?.current_tier ?? '--',
                                    })}
                                </span>
                                <span className="font-medium">
                                    {((tier_info?.profit_share_rate ?? 0) * 100).toFixed(0)}%
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm">
                                    {t('dashboard.profitShare.amountOwed')}
                                </span>
                                <span className="font-medium text-orange-600">
                                    ${(tier_info?.profit_share_owed ?? 0).toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm">{t('dashboard.profitShare.status')}</span>
                                <span
                                    className={`px-2 py-1 rounded text-xs ${
                                        tier_info?.payment_status === 'paid'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                    }`}
                                >
                                    {tier_info?.payment_status ?? '--'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Profit Status Alert */}
                <div
                    className={`p-4 rounded-lg border ${
                        profit_analysis?.is_profitable
                            ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                            : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <div
                            className={`w-2 h-2 rounded-full ${
                                profit_analysis?.is_profitable ? 'bg-green-500' : 'bg-yellow-500'
                            }`}
                        ></div>
                        <span className="font-medium">
                            {profit_analysis?.is_profitable
                                ? t('dashboard.profitShare.profitable')
                                : t('dashboard.profitShare.notProfitable')}
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                        {profit_analysis?.is_profitable
                            ? t('dashboard.profitShare.profitableDescription')
                            : t('dashboard.profitShare.notProfitableDescription')}
                    </p>
                </div>

                {tier_info?.profit_share_owed > 0 && (
                    <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg dark:bg-orange-900/20 dark:border-orange-800">
                        <div className="flex justify-between items-center">
                            <div>
                                <h5 className="font-medium text-orange-800 dark:text-orange-200">
                                    {t('dashboard.profitShare.paymentRequired')}
                                </h5>
                                <p className="text-sm text-orange-600 dark:text-orange-300">
                                    {t('dashboard.profitShare.paymentDescription', {
                                        amount: (tier_info?.profit_share_owed ?? 0).toFixed(2),
                                    })}
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => (window.location.href = '/tier')}
                                className="border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:text-orange-300"
                            >
                                {t('dashboard.profitShare.payNow')}
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
// UI feedback for blocked users
export function BlockedUserAlert() {
    const { t } = useTranslation();
    const [blocked, setBlocked] = useState(false);
    const [owed, setOwed] = useState('');
    const [tier, setTier] = useState('');

    useEffect(() => {
        // Example: fetch from backend or context
        authenticatedFetch(`${API_BASE_URL}/api/trading/blocked_status`, {
            credentials: 'include',
        })
            .then(async res => {
                if (!res.ok) {
                    return null;
                }
                return res.json();
            })
            .then(data => {
                if (data?.blocked) {
                    setBlocked(true);
                    setOwed(data.owed);
                    setTier(data.tier);
                }
            })
            .catch(error => {
                if (!isAuthExpiredError(error)) {
                    console.warn('[BlockedUserAlert] Failed to fetch blocked status:', error);
                }
            });
    }, []);

    if (!blocked) return null;
    return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <strong>{t('dashboard.blockedUser.title')}</strong>{' '}
            {t('dashboard.blockedUser.message', { tier, owed })}
        </div>
    );
}
